import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { libraryAPI, tagsAPI, boardsAPI, imagesAPI, authAPI, demoAPI, onboardingAPI } from '../api';
import { getTranslation } from '../i18n';
import EditorialGuidance from '../components/EditorialGuidance';
import DemoSpotlight from '../components/DemoSpotlight';
import StorageWarningBanner from '../components/StorageWarningBanner';
import ArchiveModeBanner from '../components/ArchiveModeBanner';
import BatchUploadStatus from '../components/BatchUploadStatus';
import { useBatchUpload } from '../hooks/useBatchUpload';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Tiny interpolation helper for i18n strings with {placeholder} tokens.
// Keeps the page's translation lookups readable without pulling in a
// heavyweight i18n runtime.
const fmt = (s, vars = {}) =>
  Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);

// Inline Tag Editor Component
const InlineTagEditor = ({ image, allTags, onSaveTags, onClose, t }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const currentTags = image.tags || [];
  const filteredSuggestions = allTags
    .filter(t => !currentTags.includes(t))
    .filter(t => t.toLowerCase().includes(inputValue.toLowerCase()))
    .slice(0, 6);

  const handleAddTag = (tag) => {
    if (!tag.trim() || currentTags.includes(tag.trim())) return;
    const newTags = [...currentTags, tag.trim()];
    onSaveTags(image.id, newTags);
    setInputValue('');
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = currentTags.filter(t => t !== tagToRemove);
    onSaveTags(image.id, newTags);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddTag(inputValue);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div 
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255,255,255,0.98)',
        padding: '8px',
        zIndex: 100,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
        borderTop: '1px solid var(--kahla-border)',
      }}
      data-testid={`inline-tag-editor-${image.id}`}
    >
      {/* Current tags with remove buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
        {currentTags.map(tag => (
          <span 
            key={tag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '10px',
              padding: '2px 6px',
              backgroundColor: 'var(--kahla-charcoal)',
              color: 'white',
            }}
          >
            {tag}
            <button
              onClick={(e) => { e.stopPropagation(); handleRemoveTag(tag); }}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: 0,
                fontSize: '12px',
                lineHeight: 1,
                opacity: 0.7,
              }}
              data-testid={`remove-tag-${tag}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      
      {/* Input with autocomplete */}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={t?.lib_add_tag_placeholder || 'Add tag...'}
          style={{
            width: '100%',
            padding: '4px 8px',
            fontSize: '11px',
            border: '1px solid var(--kahla-border)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          data-testid={`inline-tag-input-${image.id}`}
        />
        
        {/* Autocomplete suggestions */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid var(--kahla-border)',
            borderBottom: 'none',
            maxHeight: '120px',
            overflowY: 'auto',
            zIndex: 101,
          }}>
            {filteredSuggestions.map(tag => (
              <button
                key={tag}
                onClick={(e) => { e.stopPropagation(); handleAddTag(tag); setShowSuggestions(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '6px 8px',
                  fontSize: '11px',
                  textAlign: 'left',
                  border: 'none',
                  borderBottom: '1px solid var(--kahla-border)',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--kahla-cream)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                data-testid={`tag-suggestion-${tag}`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Sortable Library Image Card with Inline Tag Editing + Broken Image Handling
const SortableLibraryCard = ({ image, isSelected, onClick, reorderEnabled, allTags, onSaveTags, onDelete, onEditUrl, multiSelect, isChecked, onToggleSelect, onAddToBoard, t }) => {
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : (showTagEditor ? 50 : 1),
  };

  const dragProps = reorderEnabled ? { ...attributes, ...listeners } : {};

  const handleAddTagClick = (e) => {
    e.stopPropagation();
    setShowTagEditor(true);
  };

  const handleRemoveTag = (e, tagToRemove) => {
    e.stopPropagation();
    const newTags = (image.tags || []).filter(t => t !== tagToRemove);
    onSaveTags(image.id, newTags);
  };

  const currentTags = image.tags || [];

  return (
    <div 
      ref={setNodeRef}
      {...dragProps}
      style={{
        ...style,
        cursor: reorderEnabled ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        touchAction: reorderEnabled ? 'none' : 'auto',
        border: isSelected ? '2px solid var(--kahla-charcoal)' : '1px solid var(--kahla-border)',
        outline: reorderEnabled ? '2px dashed var(--kahla-border)' : 'none',
        outlineOffset: '-2px',
        position: 'relative',
        overflow: 'visible',
      }}
      className="image-card"
      onClick={multiSelect ? () => onToggleSelect(image.id) : (reorderEnabled ? undefined : onClick)}
      data-testid={`library-sortable-${image.id}`}
    >
      {/* Multi-select checkbox */}
      {multiSelect && (
        <div
          data-testid={`select-check-${image.id}`}
          style={{
            position: 'absolute', top: '8px', right: '8px', zIndex: 12,
            width: '24px', height: '24px',
            border: isChecked ? 'none' : '2px solid rgba(255,255,255,0.8)',
            backgroundColor: isChecked ? 'var(--kahla-charcoal)' : 'rgba(255,255,255,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', borderRadius: '3px',
          }}
        >
          {isChecked && <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', lineHeight: 1 }}>&#10003;</span>}
        </div>
      )}
      {/* Add to board button (visible on hover, not in multi-select or reorder) */}
      {!multiSelect && !reorderEnabled && !imgBroken && (
        <button
          data-testid={`add-to-board-${image.id}`}
          onClick={(e) => { e.stopPropagation(); onAddToBoard(image.id); }}
          style={{
            position: 'absolute', bottom: '8px', right: '8px', zIndex: 12,
            padding: '4px 8px', fontSize: '9px', fontWeight: '500',
            letterSpacing: '0.04em', textTransform: 'uppercase',
            backgroundColor: 'var(--kahla-charcoal)', color: 'white',
            border: 'none', cursor: 'pointer',
            opacity: 0, transition: 'opacity 0.2s', pointerEvents: 'auto',
          }}
          className="add-to-board-btn"
        >
          {t?.lib_add_to_board_btn || '+ Board'}
        </button>
      )}
      <div
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          width: '22px',
          height: '22px',
          backgroundColor: 'rgba(255,255,255,0.9)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 11,
          fontSize: '10px',
          color: 'var(--kahla-text-light)',
          opacity: 0,
          transition: 'opacity 0.2s',
          pointerEvents: 'none',
        }}
        className="drag-handle"
      >
        ⋮⋮
      </div>
      {image.is_external && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          backgroundColor: imgBroken ? 'rgba(199, 80, 80, 0.9)' : 'rgba(139, 119, 101, 0.9)',
          color: 'white',
          padding: '3px 8px',
          fontSize: '9px',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          zIndex: 1,
          fontWeight: '500'
        }}>
          {imgBroken ? (t?.lib_card_broken_link || 'Broken Link') : (t?.lib_card_external || 'External')}
        </div>
      )}
      <div style={{ 
        aspectRatio: '1', 
        overflow: 'hidden',
        position: 'relative',
      }}>
        {imgBroken ? (
          <div
            data-testid={`broken-image-${image.id}`}
            style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              backgroundColor: '#faf5f0', gap: '10px', padding: '12px',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#b8a090" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <span style={{ fontSize: '11px', color: '#8b7765', textAlign: 'center', lineHeight: '1.3' }}>
              {t?.lib_card_image_unavailable || 'Image unavailable'}
            </span>
            <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
              {image.is_external && onEditUrl && (
                <button
                  data-testid={`edit-url-card-${image.id}`}
                  onClick={(e) => { e.stopPropagation(); onEditUrl(image); }}
                  style={{
                    fontSize: '10px', padding: '3px 8px', cursor: 'pointer',
                    border: '1px solid var(--kahla-border)', background: 'white',
                    color: 'var(--kahla-text)',
                  }}
                >
                  {t?.lib_edit_url || 'Edit URL'}
                </button>
              )}
              <button
                data-testid={`remove-broken-${image.id}`}
                onClick={(e) => { e.stopPropagation(); onDelete(image); }}
                style={{
                  fontSize: '10px', padding: '3px 8px', cursor: 'pointer',
                  border: '1px solid #c7505033', background: 'white',
                  color: '#C75050',
                }}
              >
                {t?.lib_card_remove || 'Remove'}
              </button>
            </div>
          </div>
        ) : (
          <img 
            src={image.image_data} 
            alt={image.name}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgBroken(true)}
          />
        )}
      </div>
      <div style={{ padding: '10px' }}>
        <p style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginBottom: '8px' }}>
          {fmt(image.usage_count === 1 ? (t?.lib_card_used_in_n || 'Used in {count} board') : (t?.lib_card_used_in_n_plural || 'Used in {count} boards'), { count: image.usage_count })}
        </p>
        
        {/* Inline Tags with Edit Controls */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
          {currentTags.slice(0, 4).map(tag => (
            <span 
              key={tag}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                fontSize: '9px',
                padding: '2px 6px',
                backgroundColor: 'var(--kahla-cream)',
                border: '1px solid var(--kahla-border)',
                color: 'var(--kahla-text)',
              }}
            >
              {tag}
              <button
                onClick={(e) => handleRemoveTag(e, tag)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--kahla-text-light)',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '11px',
                  lineHeight: 1,
                  marginLeft: '1px',
                }}
                title={`Remove "${tag}"`}
                data-testid={`card-remove-tag-${image.id}-${tag}`}
              >
                ×
              </button>
            </span>
          ))}
          {currentTags.length > 4 && (
            <span style={{ fontSize: '9px', color: 'var(--kahla-text-light)' }}>
              +{currentTags.length - 4}
            </span>
          )}
          {/* Add Tag Button */}
          <button
            onClick={handleAddTagClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              fontSize: '14px',
              backgroundColor: 'var(--kahla-cream)',
              border: '1px solid var(--kahla-border)',
              color: 'var(--kahla-taupe)',
              cursor: 'pointer',
              padding: 0,
              borderRadius: '2px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--kahla-charcoal)'; e.target.style.color = 'white'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = 'var(--kahla-cream)'; e.target.style.color = 'var(--kahla-taupe)'; }}
            title={t?.lib_add_tag_title || 'Add tag'}
            data-testid={`add-tag-btn-${image.id}`}
          >
            +
          </button>
        </div>
      </div>
      
      {/* Inline Tag Editor Overlay */}
      {showTagEditor && (
        <InlineTagEditor
          image={image}
          allTags={allTags}
          onSaveTags={onSaveTags}
          onClose={() => setShowTagEditor(false)}
          t={t}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// PinCard — masonry / Pinterest-style card used in browse (non-reorder)
// mode. Deliberately minimal: no text below the image, no visible tags,
// no usage stats — the visual reservoir stays quiet and inspiring. All
// the metadata is a hover-reveal (or discovered via the preview modal).
//
// Aspect ratio comes from the image's own `width` / `height` fields when
// available so cards flow like Pinterest. Fallback = 4:5 (portrait) so
// external images without dimensions still get a pleasing shape.
// ═══════════════════════════════════════════════════════════════════════
const PinCard = ({
  image,
  isSelected,
  onClick,
  onDelete,
  onEditUrl,
  onAddToBoard,
  multiSelect,
  isChecked,
  onToggleSelect,
  onLongPress,
  t,
}) => {
  const [broken, setBroken] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  // Natural aspect ratio for the reserved thumbnail box. Prevents the
  // layout from jumping as images stream in.
  let aspect = '4 / 5';
  if (image.width && image.height && image.height > 0) {
    aspect = `${image.width} / ${image.height}`;
  }

  const currentTags = image.tags || [];
  const usage = image.usage_count || 0;

  const handleClick = (e) => {
    // Long-press on mobile just entered select mode — swallow this
    // click so we don't immediately open the preview modal.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (multiSelect) {
      onToggleSelect?.(image.id, e);
    } else {
      onClick?.(image);
    }
  };

  // Long-press to enter selection mode (mobile). Desktop uses the
  // toolbar "Select" button, so we only fire on `pointerdown` events
  // originating from touch input.
  const handlePointerDown = (e) => {
    if (multiSelect) return;
    if (e.pointerType !== 'touch') return;
    longPressFiredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      onLongPress?.(image.id);
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  return (
    <div
      data-testid={`library-pin-${image.id}`}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      className="library-pin-card"
      style={{
        breakInside: 'avoid',
        marginBottom: 14,
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 6,
        overflow: 'hidden',
        background: 'var(--kahla-cream)',
        boxShadow: isSelected
          ? '0 0 0 2px var(--kahla-charcoal), 0 8px 20px rgba(20,18,15,0.10)'
          : '0 1px 3px rgba(20,18,15,0.06)',
        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
      }}
    >
      {/* Multi-select checkbox — top-left, quietly present when active */}
      {multiSelect && (
        <div
          data-testid={`pin-check-${image.id}`}
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(image.id, e); }}
          style={{
            position: 'absolute', top: 10, left: 10, zIndex: 12,
            width: 22, height: 22,
            border: isChecked ? 'none' : '2px solid rgba(255,255,255,0.85)',
            background: isChecked ? 'var(--kahla-charcoal)' : 'rgba(20,18,15,0.28)',
            borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background 0.2s ease',
          }}
        >
          {isChecked && (
            <span style={{ color: 'white', fontSize: 12, lineHeight: 1 }}>&#10003;</span>
          )}
        </div>
      )}

      {/* External badge — only when the image is a URL reference */}
      {image.is_external && !broken && (
        <div
          style={{
            position: 'absolute', top: 10, right: 10, zIndex: 10,
            padding: '3px 8px',
            background: 'rgba(20,18,15,0.55)',
            color: 'rgba(255,255,255,0.92)',
            fontSize: 9,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 3,
            backdropFilter: 'blur(4px)',
          }}
        >
          {t?.lib_card_external || 'External'}
        </div>
      )}

      {/* Demo badge — flags starter imagery seeded on first visit so
          the studio never confuses it with their own uploads. Sits
          top-left when there's no multi-select checkbox competing
          for that corner. */}
      {image.is_demo && !broken && !multiSelect && (
        <div
          data-testid={`pin-demo-badge-${image.id}`}
          style={{
            position: 'absolute', top: 10, left: 10, zIndex: 10,
            padding: '3px 9px',
            background: 'rgba(201,169,110,0.92)',
            color: '#fff',
            fontSize: 9,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            borderRadius: 3,
            fontFamily: 'inherit',
          }}
        >
          {t?.lib_card_demo || 'Przykład'}
        </div>
      )}

      {/* Thumbnail box — natural aspect ratio */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: aspect }}>
        {broken ? (
          <div
            style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 8, padding: 14,
              background: '#faf5f0',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#b8a090" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <span style={{ fontSize: 11, color: '#8b7765', textAlign: 'center' }}>
              {t?.lib_card_image_unavailable || 'Image unavailable'}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              {image.is_external && onEditUrl && (
                <button
                  onClick={(e) => { e.stopPropagation(); onEditUrl(image); }}
                  style={{
                    fontSize: 10, padding: '3px 8px', cursor: 'pointer',
                    border: '1px solid var(--kahla-border)', background: 'white',
                    color: 'var(--kahla-text)',
                  }}
                >{t?.lib_edit_url || 'Edit URL'}</button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(image); }}
                style={{
                  fontSize: 10, padding: '3px 8px', cursor: 'pointer',
                  border: '1px solid #c7505033', background: 'white',
                  color: '#C75050',
                }}
              >{t?.lib_card_remove || 'Remove'}</button>
            </div>
          </div>
        ) : (
          <img
            src={image.image_data}
            alt={image.name || ''}
            loading="lazy"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover',
              display: 'block',
              transition: 'transform 0.45s ease',
            }}
            onError={() => setBroken(true)}
          />
        )}

        {/* Hover overlay — gradient veil + tiny bottom actions.
            All of it lives inside the thumbnail box so the card
            below (if we later add one) stays clean. */}
        {!broken && !multiSelect && (
          <div className="library-pin-overlay" style={{
            position: 'absolute', inset: 0,
            opacity: 0,
            transition: 'opacity 0.28s ease',
            background: 'linear-gradient(180deg, rgba(20,18,15,0.00) 30%, rgba(20,18,15,0.55) 100%)',
            pointerEvents: 'none',
          }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAddToBoard?.(image.id); }}
              data-testid={`pin-add-to-board-${image.id}`}
              style={{
                position: 'absolute', bottom: 10, right: 10,
                pointerEvents: 'auto',
                padding: '6px 12px',
                fontSize: 10,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                background: 'rgba(255,255,255,0.95)',
                color: 'var(--kahla-charcoal)',
                border: 'none',
                borderRadius: 999,
                cursor: 'pointer',
              }}
            >
              + {t?.lib_add_to_board_short || 'Board'}
            </button>
            {usage > 0 && (
              <div style={{
                position: 'absolute', bottom: 12, left: 12,
                fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.85)',
                textShadow: '0 1px 3px rgba(0,0,0,0.25)',
                pointerEvents: 'none',
              }}>
                {usage === 1
                  ? (t?.lib_pin_used_singular || 'In 1 board')
                  : fmt(t?.lib_pin_used_plural || 'In {count} boards', { count: usage })}
              </div>
            )}
            {currentTags.length > 0 && (
              <div style={{
                position: 'absolute', top: 10, left: 10,
                display: 'flex', gap: 4, flexWrap: 'wrap',
                maxWidth: 'calc(100% - 90px)',
                pointerEvents: 'none',
              }}>
                {currentTags.slice(0, 3).map((tg) => (
                  <span key={tg} style={{
                    fontSize: 9,
                    padding: '2px 7px',
                    background: 'rgba(255,255,255,0.85)',
                    color: 'var(--kahla-charcoal)',
                    borderRadius: 999,
                    letterSpacing: '0.04em',
                    textTransform: 'lowercase',
                    fontStyle: 'italic',
                  }}>{tg}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// LibraryPreviewModal — full-screen overlay opened from a pin click.
// ═══════════════════════════════════════════════════════════════════════
//
// Sits on top of the gallery — the page underneath keeps its scroll
// position and filter state so closing the modal returns the studio to
// exactly where they were. No routing involved (no URL change).
//
// Left column: cinematic image preview.
// Right column: editorial metadata rail (title, source, collections,
// tags, usage) + action row (Edit / Add to board / Download / Delete).
//
// Escape closes the modal; the outer scrim also closes on click.
const LibraryPreviewModal = ({
  image,
  onClose,
  onEdit,
  onAddToBoard,
  onDelete,
  onSaveTags,
  onEditUrl,
  onOpenBoard,
  onToggleCollection,
  allTags = [],
  collections = [],
  t = {},
}) => {
  const [downloading, setDownloading] = useState(false);
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => { setImgBroken(false); }, [image?.id]);

  // Escape → close. Registered while mounted so nested modals never leak.
  useEffect(() => {
    if (!image) return undefined;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [image, onClose]);

  // Body-scroll lock while open so wheel events don't scroll the
  // gallery underneath. The scrim absorbs its own wheel events.
  useEffect(() => {
    if (!image) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [image]);

  if (!image) return null;

  const currentTags = image.tags || [];
  const currentCollectionIds = image.collection_ids || [];
  const usage = image.usage_count || 0;
  const boardsUsing = image.boards_using || [];

  const downloadUrl = image.image_full || image.image_data;
  const suggestedFilename = image.filename || image.name ||
    `${(image.name || 'image').replace(/[^a-z0-9-]+/gi, '_')}.jpg`;

  const handleDownload = async () => {
    if (!downloadUrl) return;
    setDownloading(true);
    try {
      const res = await fetch(downloadUrl, { mode: 'cors' });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 250);
    } catch (e) {
      // Fallback: open the file in a new tab (CORS-blocked assets).
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  };

  const source = image.is_external
    ? (t.lib_preview_source_external || 'External URL')
    : (t.lib_preview_source_uploaded || 'Uploaded to library');

  return (
    <div
      data-testid="library-preview-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(20,18,15,0.72)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(12px, 3vw, 40px)',
        animation: 'fadeIn 0.25s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FBF8F2',
          width: 'min(1240px, 100%)',
          maxHeight: 'calc(100vh - 40px)',
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          borderRadius: 4,
          boxShadow: '0 30px 60px rgba(20,18,15,0.25)',
        }}
      >
        {/* Close button — top-right, subtle */}
        <button
          type="button"
          onClick={onClose}
          data-testid="library-preview-close"
          style={{
            position: 'absolute', top: 24, right: 32, zIndex: 3,
            width: 40, height: 40,
            background: 'rgba(255,255,255,0.9)',
            border: 'none', cursor: 'pointer',
            fontSize: 22, lineHeight: 1,
            color: 'var(--kahla-charcoal)',
            borderRadius: 999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
          aria-label="Close"
        >×</button>

        {/* ── Image column ─────────────────────────────────────────── */}
        <div style={{
          flex: '1 1 62%',
          background: 'var(--kahla-charcoal)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 'clamp(16px, 3vw, 32px)',
          minHeight: 300,
        }}>
          {imgBroken ? (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 12,
              color: 'rgba(255,255,255,0.7)',
            }}>
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="1" y1="1" x2="23" y2="23" />
              </svg>
              <span style={{ fontSize: 13 }}>
                {t.lib_card_image_unavailable || 'Image unavailable'}
              </span>
            </div>
          ) : (
            <img
              src={image.image_full || image.image_data}
              alt={image.name || ''}
              onError={() => setImgBroken(true)}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 120px)',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </div>

        {/* ── Metadata rail ────────────────────────────────────────── */}
        <div style={{
          flex: '0 0 clamp(320px, 34vw, 420px)',
          padding: 'clamp(24px, 3vw, 40px)',
          display: 'flex', flexDirection: 'column',
          gap: 22,
          overflow: 'auto',
        }}>
          {/* Title */}
          <div>
            <div style={{
              fontSize: 10, letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              marginBottom: 6,
            }}>
              {t.lib_preview_title_label || 'Reference'}
            </div>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 24, fontWeight: 400, lineHeight: 1.22,
              margin: 0,
              color: 'var(--kahla-charcoal)',
              wordBreak: 'break-word',
            }}>
              {image.name || image.filename || (t.lib_preview_untitled || 'Untitled reference')}
            </h2>
          </div>

          {/* Source */}
          <div>
            <div style={{
              fontSize: 10, letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              marginBottom: 6,
            }}>
              {t.lib_preview_source_label || 'Source'}
            </div>
            <div style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontStyle: 'italic',
              fontSize: 15,
              color: 'var(--kahla-text)',
            }}>
              {source}
              {image.is_external && image.external_url && (
                <div style={{ marginTop: 4, fontSize: 12, fontStyle: 'normal', color: 'var(--kahla-text-light)', wordBreak: 'break-all' }}>
                  {image.external_url}
                </div>
              )}
            </div>
          </div>

          {/* Collections — always directly editable: every collection is a
              toggle pill, filled when the image belongs to it. No edit mode. */}
          <div>
            <div style={{
              fontSize: 10, letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              marginBottom: 8,
            }}>
              {t.lib_preview_collections_label || 'Collections'}
            </div>
            {collections.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {collections.map((c) => {
                  const active = currentCollectionIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onToggleCollection?.(image.id, c.id, !active)}
                      data-testid={`library-preview-toggle-${c.id}`}
                      style={{
                        appearance: 'none',
                        border: `1px solid ${active ? 'var(--kahla-charcoal)' : 'var(--kahla-border)'}`,
                        background: active ? 'var(--kahla-charcoal)' : 'transparent',
                        color: active ? 'var(--kahla-white)' : 'var(--kahla-text)',
                        padding: '5px 12px',
                        fontSize: 10.5, letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        borderRadius: 999,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >{c.name}</button>
                  );
                })}
              </div>
            ) : (
              <span style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: 'var(--kahla-text-light)',
              }}>
                {t.lib_preview_no_collections || 'Not in any collection.'}
              </span>
            )}
          </div>

          {/* Tags */}
          <div style={{ position: 'relative' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 8,
            }}>
              <div style={{
                fontSize: 10, letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--kahla-text-light)',
              }}>
                {t.lib_preview_tags_label || 'Tags'}
              </div>
              <button
                type="button"
                onClick={() => setShowTagEditor(true)}
                data-testid="library-preview-edit-tags"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 10, letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--kahla-charcoal)',
                  padding: 0,
                }}
              >{t.lib_preview_edit || 'Edit'}</button>
            </div>
            {currentTags.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {currentTags.map((tg) => (
                  <span key={tg} style={{
                    padding: '3px 9px',
                    background: 'transparent',
                    border: '1px solid var(--kahla-border)',
                    borderRadius: 999,
                    fontSize: 10.5,
                    letterSpacing: '0.03em',
                    color: 'var(--kahla-text)',
                    fontStyle: 'italic',
                    fontFamily: 'Cormorant Garamond, serif',
                  }}>{tg}</span>
                ))}
              </div>
            ) : (
              <span style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: 'var(--kahla-text-light)',
              }}>
                {t.lib_preview_no_tags || 'No tags yet.'}
              </span>
            )}
            {showTagEditor && (
              <InlineTagEditor
                image={image}
                allTags={allTags}
                onSaveTags={(id, next) => { onSaveTags?.(id, next); }}
                onClose={() => setShowTagEditor(false)}
                t={t}
              />
            )}
          </div>

          {/* Usage / Boards */}
          <div>
            <div style={{
              fontSize: 10, letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              marginBottom: 6,
            }}>
              {t.lib_preview_usage_label || 'Usage'}
            </div>
            {usage > 0 ? (
              <div>
                <div style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic',
                  fontSize: 15,
                  color: 'var(--kahla-text)',
                  marginBottom: 6,
                }}>
                  {usage === 1
                    ? (t.lib_pin_used_singular || 'In 1 board')
                    : fmt(t.lib_pin_used_plural || 'In {count} boards', { count: usage })}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {boardsUsing.slice(0, 5).map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => onOpenBoard?.(b.id)}
                      style={{
                        textAlign: 'left',
                        background: 'none', border: 'none', padding: 0,
                        cursor: 'pointer',
                        fontSize: 12,
                        color: 'var(--kahla-charcoal)',
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                        textDecorationColor: 'rgba(20,18,15,0.25)',
                      }}
                    >{b.name}</button>
                  ))}
                  {boardsUsing.length > 5 && (
                    <span style={{ fontSize: 11, color: 'var(--kahla-text-light)' }}>
                      + {boardsUsing.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <span style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: 'var(--kahla-text-light)',
              }}>
                {t.lib_preview_unused || 'Not yet used in any board.'}
              </span>
            )}
          </div>

          {/* Actions — sits at the bottom of the rail */}
          <div style={{
            marginTop: 'auto', paddingTop: 20,
            borderTop: '1px solid var(--kahla-border)',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <button
              type="button"
              onClick={() => { onAddToBoard?.(image.id); onClose?.(); }}
              data-testid="library-preview-add-to-board"
              style={{
                width: '100%',
                padding: '11px 16px',
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                background: 'var(--kahla-charcoal)',
                color: 'var(--kahla-white)',
                border: 'none',
                cursor: 'pointer',
              }}
            >+ {t.lib_preview_action_add_to_board || 'Add to board'}</button>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
              {image.is_external && onEditUrl && (
                <button
                  type="button"
                  onClick={() => onEditUrl(image)}
                  data-testid="library-preview-edit"
                  style={{
                    padding: '9px 8px',
                    fontSize: 10, letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    background: 'transparent',
                    color: 'var(--kahla-text)',
                    border: '1px solid var(--kahla-border)',
                    cursor: 'pointer',
                  }}
                >{t.lib_preview_action_edit || 'Edit'}</button>
              )}
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                data-testid="library-preview-download"
                style={{
                  padding: '9px 8px',
                  fontSize: 10, letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'transparent',
                  color: 'var(--kahla-text)',
                  border: '1px solid var(--kahla-border)',
                  cursor: downloading ? 'wait' : 'pointer',
                  gridColumn: (image.is_external && onEditUrl) ? 'auto' : 'span 2',
                }}
              >{downloading ? '…' : (t.lib_preview_action_download || 'Download')}</button>
              <button
                type="button"
                onClick={() => { onDelete?.(image); }}
                data-testid="library-preview-delete"
                style={{
                  padding: '9px 8px',
                  fontSize: 10, letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'transparent',
                  color: '#a95050',
                  border: '1px solid #c7505033',
                  cursor: 'pointer',
                }}
              >{t.lib_preview_action_delete || 'Delete'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// BulkActionBar — bottom-anchored floating toolbar for multi-select.
// ═══════════════════════════════════════════════════════════════════════
//
// Appears when the studio has ≥ 1 image checked (or when they entered
// select mode via the toolbar / long-press but haven't picked anything
// yet). Actions: Add to Collection, Add Tags, Remove Tags, Delete.
//
// The bar renders as a `position: fixed` band centred at the bottom.
// It's calm — soft cream background, no bright colors, no icons —
// following Maestra's "quiet confidence" aesthetic.
const BulkActionBar = ({
  count,
  totalImages,
  collections = [],
  collectionStates = {},     // collectionId -> 'all' | 'some' | 'none' for current selection
  activeSelectionTags = [],  // union of tags on currently-selected images
  onAddToBoard,
  onCreateBoard,
  onToggleCollectionBulk,
  onMoveToCollection,
  onAddTags,
  onRemoveTags,
  onDelete,
  onSelectAll,
  onCancel,
  t = {},
}) => {
  // Local popover state — only one open at a time so the bar stays quiet.
  const [popover, setPopover] = useState(null); // 'coll' | 'add-tag' | 'rem-tag' | null
  const [tagDraft, setTagDraft] = useState('');
  const rootRef = useRef(null);

  // Close popover on outside click.
  useEffect(() => {
    if (!popover) return undefined;
    const handler = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setPopover(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popover]);

  const commitTagInput = () => {
    const tags = tagDraft
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!tags.length) return;
    if (popover === 'add-tag') onAddTags?.(tags);
    setTagDraft('');
    setPopover(null);
  };

  return (
    <div
      ref={rootRef}
      data-testid="library-bulk-bar"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 150,
        background: '#FBF8F2',
        border: '1px solid var(--kahla-border)',
        boxShadow: '0 12px 32px rgba(20,18,15,0.14)',
        padding: '10px 14px',
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 'calc(100vw - 32px)',
        flexWrap: 'nowrap',
        // Deliberately NOT setting `overflow` here — an `overflow: auto`
        // scrolling context on the bar clips the child popovers
        // (Add to Collection, Add Tags, Remove Tags) that render above
        // it via `position: absolute; bottom: calc(100% + 8px)`.
        // The bar's flex items shrink instead of overflowing when the
        // viewport is narrow — see `min-width: 0` on each flex child
        // below where needed.
      }}
    >
      {/* Selection count / select-all toggle */}
      <div style={{
        fontSize: 11,
        letterSpacing: '0.06em',
        color: 'var(--kahla-text)',
        padding: '0 6px',
        whiteSpace: 'nowrap',
      }}>
        <span data-testid="bulk-count" style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
        {' '}
        {count === 1
          ? (t.lib_bulk_selected_singular || 'selected')
          : (t.lib_bulk_selected_plural || 'selected')}
      </div>
      {count < totalImages && (
        <button
          type="button"
          onClick={onSelectAll}
          data-testid="bulk-select-all"
          style={{
            appearance: 'none', background: 'none', border: 'none',
            fontSize: 10, letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            cursor: 'pointer', padding: '4px 6px',
          }}
        >{t.lib_bulk_select_all || 'Select all'}</button>
      )}
      <span style={{
        width: 1, height: 20, background: 'var(--kahla-border)', margin: '0 2px',
      }} />

      {/* Add to existing board — primary action, the most-used path
          when the studio wants to attach selected inspiration to a
          specific client project. */}
      <button
        type="button"
        disabled={count === 0}
        onClick={onAddToBoard}
        data-testid="bulk-add-to-board"
        style={{
          appearance: 'none',
          background: count === 0 ? 'transparent' : 'var(--kahla-charcoal)',
          color: count === 0 ? 'var(--kahla-text-light)' : 'var(--kahla-white)',
          border: count === 0 ? '1px solid var(--kahla-border)' : 'none',
          fontSize: 10, letterSpacing: '0.1em',
          textTransform: 'uppercase',
          cursor: count === 0 ? 'not-allowed' : 'pointer',
          padding: '7px 14px',
          borderRadius: 999,
          opacity: count === 0 ? 0.5 : 1,
          transition: 'background 0.2s ease, color 0.2s ease',
        }}
      >+ {t.lib_bulk_add_to_board || 'Add to board'}</button>

      {onCreateBoard && (
        <button
          type="button"
          disabled={count === 0}
          onClick={onCreateBoard}
          data-testid="bulk-new-board"
          style={{
            appearance: 'none', background: 'none', border: 'none',
            fontSize: 10, letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: count === 0 ? 'var(--kahla-text-light)' : 'var(--kahla-text)',
            cursor: count === 0 ? 'not-allowed' : 'pointer',
            padding: '6px 10px',
            opacity: count === 0 ? 0.5 : 1,
          }}
        >{t.lib_bulk_new_board || 'New board'}</button>
      )}

      <span style={{
        width: 1, height: 20, background: 'var(--kahla-border)', margin: '0 2px',
      }} />

      {/* Manage collections — tri-state rows: click toggles add/remove
          for the whole selection; the quiet "Move" action reassigns the
          selection to one collection (confirmed in the parent). */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          disabled={count === 0}
          onClick={() => setPopover(popover === 'coll' ? null : 'coll')}
          data-testid="bulk-manage-collections"
          style={{
            appearance: 'none', background: 'none', border: 'none',
            fontSize: 10, letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: count === 0 ? 'var(--kahla-text-light)' : 'var(--kahla-text)',
            cursor: count === 0 ? 'not-allowed' : 'pointer',
            padding: '6px 10px',
            opacity: count === 0 ? 0.5 : 1,
          }}
        >{t.lib_bulk_manage_collections || 'Manage collections'}</button>
        {popover === 'coll' && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
            transform: 'translateX(-50%)',
            background: '#FBF8F2',
            border: '1px solid var(--kahla-border)',
            boxShadow: '0 12px 28px rgba(20,18,15,0.14)',
            padding: '6px 0',
            minWidth: 240,
            maxHeight: 280,
            overflowY: 'auto',
            borderRadius: 6,
          }}>
            {collections.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--kahla-text-light)' }}>
                {t.lib_bulk_no_collections || 'No collections yet.'}
              </div>
            ) : collections.map((c) => {
              const state = collectionStates[c.id] || 'none';
              return (
                <div
                  key={c.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '2px 10px 2px 14px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onToggleCollectionBulk?.(c.id, state !== 'all')}
                    data-testid={`bulk-collection-toggle-${c.id}`}
                    style={{
                      flex: 1, minWidth: 0,
                      display: 'flex', alignItems: 'center', gap: 9,
                      textAlign: 'left', padding: '7px 0',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 12, color: 'var(--kahla-text)',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      data-state={state}
                      style={{
                        width: 14, height: 14, flexShrink: 0,
                        border: '1px solid var(--kahla-charcoal)',
                        borderRadius: 3,
                        background: state === 'all' ? 'var(--kahla-charcoal)' : 'transparent',
                        color: state === 'all' ? 'var(--kahla-white)' : 'var(--kahla-charcoal)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, lineHeight: 1,
                      }}
                    >{state === 'all' ? '\u2713' : state === 'some' ? '\u2212' : ''}</span>
                    <span style={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{c.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPopover(null); onMoveToCollection?.(c.id); }}
                    data-testid={`bulk-collection-move-${c.id}`}
                    title={t.lib_bulk_move_here || 'Move'}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 9.5, letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--kahla-text-light)',
                      padding: '6px 4px', flexShrink: 0,
                    }}
                  >{t.lib_bulk_move_here || 'Move'} {'\u2192'}</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Tags */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          disabled={count === 0}
          onClick={() => setPopover(popover === 'add-tag' ? null : 'add-tag')}
          data-testid="bulk-add-tag"
          style={{
            appearance: 'none', background: 'none', border: 'none',
            fontSize: 10, letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: count === 0 ? 'var(--kahla-text-light)' : 'var(--kahla-text)',
            cursor: count === 0 ? 'not-allowed' : 'pointer',
            padding: '6px 10px',
            opacity: count === 0 ? 0.5 : 1,
          }}
        >{t.lib_bulk_add_tags || 'Add tags'}</button>
        {popover === 'add-tag' && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
            transform: 'translateX(-50%)',
            background: '#FBF8F2',
            border: '1px solid var(--kahla-border)',
            boxShadow: '0 12px 28px rgba(20,18,15,0.14)',
            padding: 10,
            width: 260,
            borderRadius: 6,
          }}>
            <input
              autoFocus
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTagInput();
                if (e.key === 'Escape') { setTagDraft(''); setPopover(null); }
              }}
              placeholder={t.lib_bulk_tag_placeholder || 'tag1, tag2, ...'}
              data-testid="bulk-tag-input"
              style={{
                width: '100%',
                padding: '7px 10px',
                fontSize: 12,
                border: '1px solid var(--kahla-border)',
                background: 'white',
                outline: 'none',
              }}
            />
            <div style={{
              marginTop: 8, display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 10, color: 'var(--kahla-text-light)' }}>
                {t.lib_bulk_tag_hint || 'Comma-separated'}
              </span>
              <button
                type="button"
                onClick={commitTagInput}
                data-testid="bulk-tag-apply"
                style={{
                  padding: '6px 12px',
                  fontSize: 10, letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'var(--kahla-charcoal)',
                  color: 'var(--kahla-white)',
                  border: 'none', cursor: 'pointer',
                }}
              >{t.lib_bulk_apply || 'Apply'}</button>
            </div>
          </div>
        )}
      </div>

      {/* Remove Tags */}
      {activeSelectionTags.length > 0 && (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setPopover(popover === 'rem-tag' ? null : 'rem-tag')}
            data-testid="bulk-remove-tag"
            style={{
              appearance: 'none', background: 'none', border: 'none',
              fontSize: 10, letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text)',
              cursor: 'pointer',
              padding: '6px 10px',
            }}
          >{t.lib_bulk_remove_tags || 'Remove tags'}</button>
          {popover === 'rem-tag' && (
            <div style={{
              position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
              transform: 'translateX(-50%)',
              background: '#FBF8F2',
              border: '1px solid var(--kahla-border)',
              boxShadow: '0 12px 28px rgba(20,18,15,0.14)',
              padding: '6px 0',
              minWidth: 200,
              maxHeight: 260,
              overflowY: 'auto',
              borderRadius: 6,
            }}>
              {activeSelectionTags.map((tg) => (
                <button
                  key={tg}
                  type="button"
                  onClick={() => { onRemoveTags?.([tg]); setPopover(null); }}
                  data-testid={`bulk-remove-tag-${tg}`}
                  style={{
                    width: '100%', textAlign: 'left', padding: '8px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, color: 'var(--kahla-text)',
                    fontFamily: 'inherit',
                  }}
                >{tg}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <span style={{
        width: 1, height: 20, background: 'var(--kahla-border)', margin: '0 2px',
      }} />

      {/* Delete */}
      <button
        type="button"
        disabled={count === 0}
        onClick={onDelete}
        data-testid="bulk-delete"
        style={{
          appearance: 'none', background: 'none', border: 'none',
          fontSize: 10, letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: count === 0 ? '#c7505066' : '#a95050',
          cursor: count === 0 ? 'not-allowed' : 'pointer',
          padding: '6px 10px',
        }}
      >{t.lib_bulk_delete || 'Delete'}</button>

      <span style={{
        width: 1, height: 20, background: 'var(--kahla-border)', margin: '0 2px',
      }} />

      {/* Cancel */}
      <button
        type="button"
        onClick={onCancel}
        data-testid="bulk-cancel"
        style={{
          appearance: 'none', background: 'none', border: 'none',
          fontSize: 10, letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
          cursor: 'pointer',
          padding: '6px 10px',
        }}
      >{t.lib_bulk_cancel || 'Cancel'}</button>
    </div>
  );
};





// Detail sidebar image with broken state handling
const DetailImage = ({ src, alt, isExternal, onEditUrl, onDelete, t }) => {
  const [broken, setBroken] = useState(false);
  
  // Reset broken state when src changes
  useEffect(() => {
    setBroken(false);
  }, [src]);
  
  if (broken) {
    return (
      <div 
        data-testid="detail-broken-image"
        style={{
          width: '100%', height: '100%', 
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: '12px', padding: '20px',
        }}
      >
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#b8a090" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="1" y1="1" x2="23" y2="23" />
        </svg>
        <span style={{ fontSize: '13px', color: '#8b7765', textAlign: 'center' }}>
          {t?.lib_card_image_unavailable || 'Image unavailable'}
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isExternal && onEditUrl && (
            <button
              data-testid="detail-edit-url-btn"
              onClick={onEditUrl}
              style={{
                fontSize: '11px', padding: '5px 12px', cursor: 'pointer',
                border: '1px solid var(--kahla-border)', background: 'white',
                color: 'var(--kahla-text)',
              }}
            >
              {t?.lib_edit_url || 'Edit URL'}
            </button>
          )}
          <button
            data-testid="detail-remove-broken-btn"
            onClick={onDelete}
            style={{
              fontSize: '11px', padding: '5px 12px', cursor: 'pointer',
              border: '1px solid #c7505033', background: 'white',
              color: '#C75050',
            }}
          >
            {t?.lib_card_remove || 'Remove'}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <img 
      src={src} alt={alt}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={() => setBroken(true)}
    />
  );
};

// ═══════════════════════════════════════════════════════════════════════
// CollectionRail — top-level organisation strip above the library grid.
// ═══════════════════════════════════════════════════════════════════════
//
// Renders "All → Uncategorized → <owner's collections> → + New" as
// pill-shaped chips. Selecting one scopes every subsequent request to
// that collection. Chips are quiet by default (transparent bg, taupe
// text); the active chip fills with charcoal so the studio always knows
// where they are.
//
// A subtle right-side "..." reveals rename / delete on hover for
// non-default collections. Renaming happens in-place with a contentEditable
// span so the interaction feels editorial, not admin-panel.
// Small pill-shaped filter chip used by the CollectionRail. Extracted
// so React doesn't recreate the component identity on every rail render
// (would trash all child state, including the actions "..." menu).
const CollectionChip = ({ selected, count, children, onClick, testid, tail }) => (
  <div style={{ position: 'relative', display: 'inline-flex' }}>
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={selected ? 'true' : 'false'}
      style={{
        appearance: 'none',
        border: `1px solid ${selected ? 'var(--kahla-charcoal)' : 'var(--kahla-taupe)'}`,
        background: selected ? 'var(--kahla-charcoal)' : 'var(--kahla-cream)',
        color: selected ? 'var(--kahla-white)' : 'var(--kahla-text)',
        padding: '9px 16px',
        paddingRight: tail ? 34 : 16,
        fontSize: '11.5px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'background 0.25s ease, color 0.25s ease, border-color 0.25s ease',
        whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}
    >
      <span>{children}</span>
      {typeof count === 'number' && (
        <span style={{
          fontSize: 10,
          opacity: selected ? 0.75 : 0.55,
          fontVariantNumeric: 'tabular-nums',
        }}>{count}</span>
      )}
    </button>
    {tail}
  </div>
);

const CollectionRail = ({
  collections = [],
  activeCollection,          // null | '' | id
  onSelect,
  totalAll = 0,
  uncategorizedCount = 0,
  onCreate,
  onRename,
  onDelete,
  t = {},
}) => {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const inputRef = useRef(null);
  const renameRef = useRef(null);

  useEffect(() => {
    if (creating && inputRef.current) inputRef.current.focus();
  }, [creating]);
  useEffect(() => {
    if (renamingId && renameRef.current) {
      renameRef.current.focus();
      renameRef.current.select();
    }
  }, [renamingId]);

  const commitCreate = () => {
    const value = draft.trim();
    if (value) onCreate?.(value);
    setCreating(false);
    setDraft('');
  };
  const commitRename = (id) => {
    const value = renameDraft.trim();
    if (value) onRename?.(id, value);
    setRenamingId(null);
    setRenameDraft('');
  };

  return (
    <div
      data-testid="library-collection-rail"
      data-onboarding="library_collection_rail"
      style={{ marginBottom: 24 }}
    >
      <div style={{
        fontSize: 11,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: 'var(--kahla-charcoal)',
        fontWeight: 500,
        marginBottom: 10,
      }}>
        {t.lib_collections_heading || 'Collections'}
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}>
      <CollectionChip
        selected={activeCollection === null || activeCollection === undefined}
        count={totalAll}
        onClick={() => onSelect(null)}
        testid="collection-chip-all"
      >
        {t.lib_collection_all || 'All'}
      </CollectionChip>

      {collections.map((c) => {
        const selected = activeCollection === c.id;
        const isCustom = !c.is_default;
        return renamingId === c.id ? (
          <input
            key={c.id}
            ref={renameRef}
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onBlur={() => commitRename(c.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename(c.id);
              if (e.key === 'Escape') { setRenamingId(null); setRenameDraft(''); }
            }}
            data-testid={`collection-rename-input-${c.id}`}
            style={{
              padding: '8px 14px',
              fontSize: '11.5px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              border: '1px solid var(--kahla-charcoal)',
              background: 'var(--kahla-white)',
              color: 'var(--kahla-text)',
              borderRadius: 6,
              outline: 'none',
              minWidth: 120,
            }}
          />
        ) : (
          <CollectionChip
            key={c.id}
            selected={selected}
            count={c.image_count}
            onClick={() => onSelect(c.id)}
            testid={`collection-chip-${c.id}`}
            tail={
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpenFor(menuOpenFor === c.id ? null : c.id);
                }}
                data-testid={`collection-menu-${c.id}`}
                style={{
                  position: 'absolute',
                  right: 6, top: '50%', transform: 'translateY(-50%)',
                  width: 22, height: 22,
                  borderRadius: 999,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: selected ? 'rgba(255,255,255,0.72)' : 'var(--kahla-text-light)',
                  fontSize: 15,
                  lineHeight: 1,
                  padding: 0,
                }}
                aria-label="Collection actions"
              >⋯</button>
            }
          >
            {c.name}
            {menuOpenFor === c.id && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  background: '#FBF8F2',
                  border: '1px solid var(--kahla-border)',
                  boxShadow: '0 12px 28px rgba(20,18,15,0.10)',
                  padding: '4px 0',
                  minWidth: 160,
                  zIndex: 10,
                  textTransform: 'none',
                  letterSpacing: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setRenamingId(c.id);
                    setRenameDraft(c.name);
                    setMenuOpenFor(null);
                  }}
                  data-testid={`collection-rename-${c.id}`}
                  style={{
                    width: '100%', textAlign: 'left', padding: '9px 14px',
                    background: 'none', border: 'none',
                    fontSize: 12, color: 'var(--kahla-text)',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {t.lib_collection_rename || 'Rename'}
                </button>
                {isCustom && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(t.lib_collection_delete_confirm ||
                        `Delete "${c.name}"? Images stay in the library.`)) {
                        onDelete?.(c.id);
                      }
                      setMenuOpenFor(null);
                    }}
                    data-testid={`collection-delete-${c.id}`}
                    style={{
                      width: '100%', textAlign: 'left', padding: '9px 14px',
                      background: 'none', border: 'none',
                      fontSize: 12, color: '#a95050',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {t.lib_collection_delete || 'Delete'}
                  </button>
                )}
              </div>
            )}
          </CollectionChip>
        );
      })}

      {uncategorizedCount > 0 && (
        <CollectionChip
          selected={activeCollection === ''}
          count={uncategorizedCount}
          onClick={() => onSelect('')}
          testid="collection-chip-uncategorized"
        >
          {t.lib_collection_uncategorized || 'Uncategorized'}
        </CollectionChip>
      )}

      {creating ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitCreate}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitCreate();
            if (e.key === 'Escape') { setCreating(false); setDraft(''); }
          }}
          placeholder={t.lib_collection_new_placeholder || 'Collection name'}
          data-testid="collection-create-input"
          style={{
            padding: '8px 14px',
            fontSize: '11.5px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            border: '1px dashed var(--kahla-charcoal)',
            background: 'transparent',
            color: 'var(--kahla-text)',
            borderRadius: 6,
            outline: 'none',
            minWidth: 160,
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="collection-create-btn"
          style={{
            appearance: 'none',
            border: '1px dashed var(--kahla-taupe)',
            background: 'transparent',
            color: 'var(--kahla-text-light)',
            padding: '9px 14px',
            fontSize: '11.5px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'color 0.25s ease, border-color 0.25s ease',
          }}
        >+ {t.lib_collection_new || 'New collection'}</button>
      )}
      </div>
    </div>
  );
};


const ImageLibrary = () => {
  const navigate = useNavigate();
  document.title = 'Maestra - Library';

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  // Full-screen Library Preview Modal (Phase 3). Separate state from
  // `selectedImage` (which powers the reorder-mode sidebar) so the
  // modal never fights with the sidebar for a single source of truth.
  const [previewImage, setPreviewImage] = useState(null);
  // Duplicates finder — populated by GET /library/duplicates. The
  // modal is a two-step flow: preview counts → confirm → dedupe.
  const [duplicatesReport, setDuplicatesReport] = useState(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicatesRunning, setDuplicatesRunning] = useState(false);
  const [filter, setFilter] = useState(null);
  const [toast, setToast] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [counts, setCounts] = useState({ total_all: 0, total_uploaded: 0, total_external: 0 });

  // Details panel visibility — separate from `selectedImage` so the
  // founder can DISMISS the panel without losing the selection. Defaults
  // to collapsed on narrow viewports (<1100px) so small laptops aren't
  // cramped by a permanent metadata sidebar.
  const [detailsOpen, setDetailsOpen] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1100 : true
  );
  // When the founder explicitly closes the panel, remember that intent
  // until they explicitly re-open it. A fresh image click reopens the
  // panel on the desktop layout but never on mobile (kept dismissed
  // until the toggle is tapped).
  const [detailsDismissed, setDetailsDismissed] = useState(false);

  // Track viewport so the details panel behaves as a drawer on small
  // screens but a sticky sidebar on desktop. ~1100px matches the
  // breakpoint where the 300px sidebar starts crowding the grid.
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1100 : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsNarrow(window.innerWidth < 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Click on an image card: select it. The behaviour differs by viewport.
  //   • Desktop: the details panel is sticky on the right. Selecting an
  //     image reopens it unless the founder has explicitly dismissed.
  //   • Narrow/mobile: open the details as a bottom sheet overlay every
  //     time, so the founder never has to scroll back to a top "Show
  //     Details" toggle after tapping an image deep in the grid.
  const handleSelectImage = useCallback((image) => {
    setSelectedImage(image);
    if (isNarrow) {
      setDetailsOpen(true);
      setDetailsDismissed(false);
    } else if (!detailsDismissed) {
      setDetailsOpen(true);
    }
  }, [isNarrow, detailsDismissed]);

  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
    setDetailsDismissed(true);
  }, []);

  const openDetails = useCallback(() => {
    setDetailsOpen(true);
    setDetailsDismissed(false);
  }, []);
  
  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [activeTags, setActiveTags] = useState([]);
  const [tagQuery, setTagQuery] = useState('');
  // ── Library Collections (higher-level organisation) ───────────────
  // `activeCollection`:
  //   null  → "All" (no filter)
  //   ''    → "Uncategorized" (images not in any collection)
  //   <id>  → single collection filter
  const [collections, setCollections] = useState([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [totalAllCount, setTotalAllCount] = useState(0);
  const [activeCollection, setActiveCollection] = useState(null);
  const [uploadTags, setUploadTags] = useState('');
  const [editingTags, setEditingTags] = useState(false);
  const [editTagInput, setEditTagInput] = useState('');
  const [exportingTags, setExportingTags] = useState(false);
  
  // Infinite scroll sentinel ref
  const sentinelRef = useRef(null);
  
  // Duplicate detection state
  const [dupModal, setDupModal] = useState(null); // { file, existing, pendingFiles }
  const [dupTagInput, setDupTagInput] = useState('');
  
  // Multi-select & board workflow state
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [boardSelectorModal, setBoardSelectorModal] = useState(null); // 'single' | 'multi'
  const [boardSelectorImageId, setBoardSelectorImageId] = useState(null);
  const [createBoardModal, setCreateBoardModal] = useState(false);
  const [boardsList, setBoardsList] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  
  // Branding
  const [branding, setBranding] = useState(null);
  const [workspaceLang, setWorkspaceLang] = useState('en');
  // Resolved translation bundle for the current workspace language.
  // Falls back to English automatically inside getTranslation when the
  // requested locale is missing.
  const t = getTranslation(workspaceLang);
  const [archiveMode, setArchiveMode] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [dismissedGuidance, setDismissedGuidance] = useState([]);
  const [librarySpotlightActive, setLibrarySpotlightActive] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardClient, setNewBoardClient] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [addingToBoard, setAddingToBoard] = useState(false);
  const [successBoard, setSuccessBoard] = useState(null);
  // External URL state
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [urlPreview, setUrlPreview] = useState(null);
  const [urlError, setUrlError] = useState(null);
  const [addingExternal, setAddingExternal] = useState(false);

  // ── Two-step upload batch state ───────────────────────────────────
  // Files dropped/chosen are staged here instead of uploading
  // immediately. The "Save to Library" step finalises everything in
  // one pass and applies the same tag set to every image — files and
  // the external link alike. Keeps the legacy duplicate-detection +
  // batch-upload coordinator untouched; only the entry point changes.
  const [pendingFiles, setPendingFiles] = useState([]); // File[]
  
  // Edit URL state
  const [editingUrl, setEditingUrl] = useState(false);
  const [editUrlValue, setEditUrlValue] = useState('');
  const [editUrlPreview, setEditUrlPreview] = useState(null);
  const [editUrlError, setEditUrlError] = useState(null);
  const [savingUrl, setSavingUrl] = useState(false);
  
  // Reorder mode state - disabled by default for mobile-friendly scrolling
  const [reorderMode, setReorderMode] = useState(false);

  // Drag & drop sensors - only active in reorder mode
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Increased distance to avoid accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchImages = useCallback(async (reset = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(1);
      }
      const tagParam = activeTags.length > 0 ? activeTags.join(',') : null;
      const { data } = await libraryAPI.list(filter, 1, 60, tagParam, activeCollection);
      setImages(data.images);
      setHasMore(data.has_more);
      setCounts(data.counts);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error fetching library:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, activeTags, activeCollection]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const tagParam = activeTags.length > 0 ? activeTags.join(',') : null;
      const { data } = await libraryAPI.list(filter, nextPage, 60, tagParam, activeCollection);
      setImages(prev => [...prev, ...data.images]);
      setHasMore(data.has_more);
      setCounts(data.counts);
      setCurrentPage(nextPage);
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [filter, activeTags, activeCollection, currentPage, loadingMore, hasMore]);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await tagsAPI.getAll();
      setAllTags(data.tags || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  }, []);

  const fetchCollections = useCallback(async () => {
    try {
      const { data } = await libraryAPI.collections.list();
      setCollections(data.collections || []);
      setUncategorizedCount(data.uncategorized_count || 0);
      setTotalAllCount(data.total_all || 0);
    } catch (err) {
      // Silent — collections are an enhancement; the library still
      // works without them (falls back to "All").
      console.warn('Failed to load library collections', err);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  useEffect(() => {
    authAPI.getSettings().then(({ data }) => {
      if (data.branding) setBranding(data.branding);
      if (data.language) setWorkspaceLang(data.language);
      if (data.archive_mode) setArchiveMode(data.archive_mode);
    }).catch(() => {});
    demoAPI.getGuidance()
      .then(res => setGuidance(res.data?.guidance || null))
      .catch(() => {});
    onboardingAPI.state()
      .then(res => {
        const dg = res.data?.dismissed_guidance || [];
        setDismissedGuidance(dg);
        // Auto-arm the library spotlight on first visit, unless overridden
        // by ?preview_onboarding=1 (admin preview).
        const previewMode = typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('preview_onboarding') === '1';
        if (previewMode) {
          setTimeout(() => setLibrarySpotlightActive(true), 600);
        } else if (!dg.includes('library_spotlight')) {
          setTimeout(() => setLibrarySpotlightActive(true), 700);
        }
      })
      .catch(() => {});
  }, []);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );
    
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  // Compute SHA-256 hash of a file in the browser
  const computeFileHash = async (file) => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Shared batch uploader for the multi-file library upload path. The
  // single-file path with dedup confirmation continues to use the
  // existing modal flow.
  const libraryTagsRef = useRef('');
  // Captured at batch-start time so every file in this batch shares one
  // anchor — eliminates the concurrent-lookup race documented in
  // `_next_board_order`. See BoardEditor for the same pattern.
  const libraryBaseOrderRef = useRef(0);
  const libraryBatch = useBatchUpload({
    uploadFn: async (file, index) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      if (libraryTagsRef.current) formData.append('tags', libraryTagsRef.current);
      formData.append('selection_index', String(libraryBaseOrderRef.current + index));
      return libraryAPI.upload(formData);
    },
    concurrency: 5,
    onAllDone: () => {
      fetchImages();
      fetchTags();
    },
  });

  // Drop / Choose-files entry. Files are staged into `pendingFiles`
  // so the studio can review the selection and assign tags before
  // committing. The actual upload runs from `commitBatch()`.
  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    setPendingFiles(prev => {
      // Preserve order, dedupe by name+size+lastModified to avoid
      // accidental double-drops of the same file.
      const seen = new Set(prev.map(f => `${f.name}::${f.size}::${f.lastModified}`));
      const merged = [...prev];
      acceptedFiles.forEach(f => {
        const key = `${f.name}::${f.size}::${f.lastModified}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(f);
        }
      });
      return merged;
    });
  }, []);

  // Remove a single staged file by index.
  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Clear everything staged for upload — files, link, preview, tags.
  const clearBatch = () => {
    setPendingFiles([]);
    setShowUrlInput(false);
    setExternalUrl('');
    setUrlPreview(null);
    setUrlError(null);
    setUploadTags('');
  };

  // Commit the staged batch. Runs the existing duplicate-check +
  // batch-upload flow for files, then adds the external link (if any)
  // with the same tag set. Tags applied are taken from `uploadTags`
  // at the moment of save, so quick-tag chips that the studio clicked
  // are honoured even if they typed the trailing comma differently.
  const commitBatch = useCallback(async () => {
    const hasFiles = pendingFiles.length > 0;
    const hasLink = !!urlPreview && !urlError && externalUrl.trim();
    if (!hasFiles && !hasLink) return;

    // Snapshot the tag string and parse into a clean list for the
    // external-link payload (file uploads still receive the raw CSV
    // through the existing libraryTagsRef path).
    const tagsCsv = uploadTags.trim();
    const tagList = tagsCsv
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    // 1. Files — reuse existing batched upload path so duplicate
    //    detection, retry, and batch progress all keep working.
    if (hasFiles) {
      setUploading(true);
      try {
        const filesToProcess = [...pendingFiles];
        const firstFile = filesToProcess[0];
        const hash = await computeFileHash(firstFile);
        const { data: checkResult } = await libraryAPI.checkHash(hash);

        if (checkResult.duplicate && checkResult.existing) {
          setDupModal({
            file: firstFile,
            existing: checkResult.existing,
            pendingFiles: filesToProcess.slice(1),
            hash,
          });
          setDupTagInput(tagsCsv);
          setUploading(false);
          // Do not clear staged files yet — the duplicate flow needs
          // them. The duplicate handlers clear pendingFiles on success.
          return;
        }

        setUploading(false);
        libraryTagsRef.current = tagsCsv;
        try {
          const { data: meta } = await libraryAPI.nextOrder();
          libraryBaseOrderRef.current = meta?.next_order ?? 0;
        } catch (e) {
          libraryBaseOrderRef.current = 0;
        }
        await libraryBatch.start(filesToProcess);
        setPendingFiles([]);
      } catch (err) {
        console.error('Error uploading:', err);
        const detail = err?.response?.data?.detail;
        const message = (detail && typeof detail === 'object') ? detail.message : (detail || t.toast_upload_failed);
        showToast(message);
        setUploading(false);
        return;
      }
    }

    // 2. External link — same tag set.
    if (hasLink) {
      setAddingExternal(true);
      try {
        await libraryAPI.addExternal(externalUrl.trim(), null, tagList);
        setExternalUrl('');
        setUrlPreview(null);
        setShowUrlInput(false);
        fetchImages();
        showToast(t.toast_external_added);
      } catch (err) {
        console.error('Error adding external:', err);
        setUrlError(err.response?.data?.detail || t.toast_external_add_failed);
        setAddingExternal(false);
        return;
      } finally {
        setAddingExternal(false);
      }
    }

    // Reset tag input once the batch has been handed off.
    setUploadTags('');
    fetchTags();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFiles, urlPreview, urlError, externalUrl, uploadTags, t]);

  // Handle "Use existing" from duplicate modal
  const handleDupUseExisting = async () => {
    const { existing, pendingFiles } = dupModal;
    const newTags = dupTagInput.split(',').map(t => t.trim()).filter(Boolean);
    
    // Merge tags if provided
    if (newTags.length > 0) {
      const merged = [...new Set([...(existing.tags || []), ...newTags])];
      try {
        await tagsAPI.updateImageTags(existing.id, merged);
      } catch (e) {
        console.error('Failed to update tags:', e);
      }
    }
    
    setDupModal(null);
    showToast(t.toast_image_reused);
    
    // Continue uploading remaining files
    if (pendingFiles && pendingFiles.length > 0) {
      setUploading(true);
      try {
        for (const file of pendingFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('name', file.name);
          if (uploadTags.trim()) {
            formData.append('tags', uploadTags.trim());
          }
          await libraryAPI.upload(formData);
        }
        showToast(fmt(t.toast_more_images_added, { count: pendingFiles.length }));
      } catch (err) {
        const detail = err?.response?.data?.detail;
        showToast(detail || t.toast_some_uploads_failed);
      } finally {
        setUploading(false);
      }
    }
    
    fetchImages();
    fetchTags();
    setUploadTags('');
    setPendingFiles([]);
  };

  // Handle "Upload anyway" from duplicate modal
  const handleDupUploadAnyway = async () => {
    const { file, pendingFiles } = dupModal;
    setDupModal(null);
    setUploading(true);
    
    try {
      const allFiles = [file, ...(pendingFiles || [])];
      for (const f of allFiles) {
        const formData = new FormData();
        formData.append('file', f);
        formData.append('name', f.name);
        formData.append('force', 'true');
        if (uploadTags.trim()) {
          formData.append('tags', uploadTags.trim());
        }
        await libraryAPI.upload(formData);
      }
      fetchImages();
      fetchTags();
      setUploadTags('');
      setPendingFiles([]);
      showToast(fmt(t.toast_images_added, { count: allFiles.length }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(detail || t.toast_upload_failed);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic', '.heif'] },
  });

  // ==================== BOARD WORKFLOW HANDLERS ====================

  const fetchBoardsList = async () => {
    setBoardsLoading(true);
    try {
      const { data } = await boardsAPI.list();
      setBoardsList(data.filter(b => b.current_status !== 'ARCHIVED'));
    } catch { setBoardsList([]); }
    finally { setBoardsLoading(false); }
  };

  // Remembers the last id the studio clicked while in selection mode
  // so shift+click can extend the range from anchor → clicked pin.
  const lastSelectedIdRef = useRef(null);

  const toggleSelectImage = (imageId, event) => {
    // Shift-click on desktop → range select from the last anchor to
    // the clicked pin. Falls back to a normal toggle when there's no
    // anchor yet or the target is outside the current grid.
    if (event && event.shiftKey && lastSelectedIdRef.current) {
      const anchor = lastSelectedIdRef.current;
      const ids = images.map((img) => img.id);
      const a = ids.indexOf(anchor);
      const b = ids.indexOf(imageId);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const range = ids.slice(lo, hi + 1);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          range.forEach((id) => next.add(id));
          return next;
        });
        lastSelectedIdRef.current = imageId;
        return;
      }
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
    lastSelectedIdRef.current = imageId;
  };

  const handleSelectAll = () => {
    if (selectedIds.size === images.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(images.map(i => i.id)));
    }
  };

  const exitMultiSelect = () => {
    setMultiSelect(false);
    setSelectedIds(new Set());
    lastSelectedIdRef.current = null;
  };

  // Bulk-assign helpers powering the BulkActionBar (Phase 4).
  const handleBulkAddToCollection = async (collectionId) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    try {
      await libraryAPI.bulkAssign({
        image_ids: ids,
        add_collection_ids: [collectionId],
      });
      setImages((prev) => prev.map((img) => {
        if (!ids.includes(img.id)) return img;
        const set = new Set(img.collection_ids || []);
        set.add(collectionId);
        return { ...img, collection_ids: Array.from(set) };
      }));
      fetchCollections();
      const label = collections.find((c) => c.id === collectionId)?.name || '';
      showToast(fmt(t.toast_bulk_added_to_collection ||
        'Added {count} to {name}', { count: ids.length, name: label }));
    } catch (err) {
      showToast(err?.response?.data?.detail || t.toast_bulk_action_failed || 'Bulk update failed');
    }
  };

  const handleBulkRemoveFromCollection = async (collectionId) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    try {
      await libraryAPI.bulkAssign({
        image_ids: ids,
        remove_collection_ids: [collectionId],
      });
      setImages((prev) => prev.map((img) => {
        if (!ids.includes(img.id)) return img;
        const set = new Set(img.collection_ids || []);
        set.delete(collectionId);
        return { ...img, collection_ids: Array.from(set) };
      }));
      fetchCollections();
      const label = collections.find((c) => c.id === collectionId)?.name || '';
      showToast(fmt(t.toast_bulk_removed_from_collection ||
        'Removed {count} from {name}', { count: ids.length, name: label }));
      if (activeCollection === collectionId) fetchImages();
    } catch (err) {
      showToast(err?.response?.data?.detail || t.toast_bulk_action_failed || 'Bulk update failed');
    }
  };

  const handleBulkToggleCollection = (collectionId, shouldAdd) => (
    shouldAdd ? handleBulkAddToCollection(collectionId) : handleBulkRemoveFromCollection(collectionId)
  );

  // Move = assign to exactly one collection, stripping every other
  // collection the selected images currently belong to. Destructive-ish,
  // so it asks for explicit confirmation first.
  const handleBulkMoveToCollection = async (collectionId) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    const label = collections.find((c) => c.id === collectionId)?.name || '';
    const others = new Set();
    images.forEach((img) => {
      if (!selectedIds.has(img.id)) return;
      (img.collection_ids || []).forEach((cid) => { if (cid !== collectionId) others.add(cid); });
    });
    const ok = window.confirm(fmt(t.lib_bulk_move_confirm ||
      'Move {count} image(s) to "{name}"? They will be removed from all other collections.',
      { count: ids.length, name: label }));
    if (!ok) return;
    try {
      await libraryAPI.bulkAssign({
        image_ids: ids,
        add_collection_ids: [collectionId],
        remove_collection_ids: [...others],
      });
      setImages((prev) => prev.map((img) => (
        ids.includes(img.id) ? { ...img, collection_ids: [collectionId] } : img
      )));
      fetchCollections();
      showToast(fmt(t.toast_bulk_moved_to_collection ||
        'Moved {count} to {name}', { count: ids.length, name: label }));
      if (activeCollection && activeCollection !== collectionId) fetchImages();
    } catch (err) {
      showToast(err?.response?.data?.detail || t.toast_bulk_action_failed || 'Bulk update failed');
    }
  };

  const handleBulkAddTags = async (tagList) => {
    if (selectedIds.size === 0 || tagList.length === 0) return;
    const ids = [...selectedIds];
    try {
      await libraryAPI.bulkAssign({ image_ids: ids, add_tags: tagList });
      setImages((prev) => prev.map((img) => {
        if (!ids.includes(img.id)) return img;
        const set = new Set(img.tags || []);
        tagList.forEach((tg) => set.add(tg));
        return { ...img, tags: Array.from(set) };
      }));
      fetchTags();
      showToast(fmt(t.toast_bulk_tags_added ||
        'Tagged {count} images', { count: ids.length }));
    } catch (err) {
      showToast(err?.response?.data?.detail || t.toast_bulk_action_failed || 'Bulk update failed');
    }
  };

  const handleBulkRemoveTags = async (tagList) => {
    if (selectedIds.size === 0 || tagList.length === 0) return;
    const ids = [...selectedIds];
    try {
      await libraryAPI.bulkAssign({ image_ids: ids, remove_tags: tagList });
      setImages((prev) => prev.map((img) => {
        if (!ids.includes(img.id)) return img;
        const set = new Set(img.tags || []);
        tagList.forEach((tg) => set.delete(tg));
        return { ...img, tags: Array.from(set) };
      }));
      fetchTags();
      showToast(fmt(t.toast_bulk_tags_removed ||
        'Removed tags from {count} images', { count: ids.length }));
    } catch (err) {
      showToast(err?.response?.data?.detail || t.toast_bulk_action_failed || 'Bulk update failed');
    }
  };

  // Duplicates finder — GET /library/duplicates then present a
  // confirmation modal that shows how many groups + total duplicates
  // will be removed. Only the confirm step calls the dedupe endpoint,
  // so opening the finder is a safe read.
  const handleOpenDuplicatesFinder = async () => {
    setDuplicatesLoading(true);
    setDuplicatesReport({ open: true, report: null });
    try {
      const { data } = await libraryAPI.duplicates.list();
      setDuplicatesReport({ open: true, report: data });
    } catch (err) {
      setDuplicatesReport(null);
      showToast(err?.response?.data?.detail || (t.toast_duplicates_failed || 'Could not scan for duplicates.'));
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleConfirmDedupe = async () => {
    setDuplicatesRunning(true);
    try {
      const { data } = await libraryAPI.duplicates.dedupe();
      setDuplicatesReport(null);
      fetchImages();
      fetchCollections();
      showToast(fmt(t.toast_duplicates_removed ||
        'Removed {count} duplicates.', { count: data.removed_count }));
    } catch (err) {
      showToast(err?.response?.data?.detail || (t.toast_duplicates_failed || 'Dedupe failed.'));
    } finally {
      setDuplicatesRunning(false);
    }
  };



  // Open board selector for a single image
  const handleAddToBoard = (imageId) => {
    setBoardSelectorImageId(imageId);
    setBoardSelectorModal('single');
    fetchBoardsList();
  };

  // Open board selector for multi-selected images
  const handleAddSelectedToBoard = () => {
    if (selectedIds.size === 0) return;
    setBoardSelectorModal('multi');
    fetchBoardsList();
  };

  // Add images to an existing board
  const handleConfirmAddToBoard = async (boardId) => {
    setAddingToBoard(true);
    try {
      const imageIds = boardSelectorModal === 'single'
        ? [boardSelectorImageId]
        : [...selectedIds];
      
      let added = 0;
      for (const imgId of imageIds) {
        try {
          await imagesAPI.addFromLibrary(boardId, imgId);
          added++;
        } catch (e) {
          console.error(`Failed to add image ${imgId}:`, e);
        }
      }
      
      const board = boardsList.find(b => b.id === boardId);
      const boardName = board ? board.name : 'board';
      setBoardSelectorModal(null);
      setBoardSelectorImageId(null);
      showToast(fmt(added === 1 ? t.toast_added_to_board_one : t.toast_added_to_board_many, { count: added, board: boardName }));
      if (boardSelectorModal === 'multi') exitMultiSelect();
    } catch (err) {
      showToast(t.toast_add_to_board_failed);
    } finally {
      setAddingToBoard(false);
    }
  };

  // Open create board modal
  const handleOpenCreateBoard = () => {
    setBoardSelectorModal(null);
    setCreateBoardModal(true);
    setNewBoardName('');
    setNewBoardClient('');
    setSuccessBoard(null);
  };

  // Create board + add selected images
  const handleCreateBoardFromSelection = async () => {
    if (!newBoardName.trim()) return;
    setCreatingBoard(true);
    try {
      const { data: board } = await boardsAPI.create({
        name: newBoardName.trim(),
        client_name: newBoardClient.trim() || '',
        expiration_days: 14,
      });
      
      const imageIds = multiSelect ? [...selectedIds] : (boardSelectorImageId ? [boardSelectorImageId] : []);
      let added = 0;
      for (const imgId of imageIds) {
        try {
          await imagesAPI.addFromLibrary(board.id, imgId);
          added++;
        } catch (e) {
          console.error(`Failed to add image ${imgId}:`, e);
        }
      }
      
      setSuccessBoard({ id: board.id, name: board.name, count: added });
      exitMultiSelect();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'board_limit_reached') {
        showToast(t.toast_board_limit_reached);
      } else {
        showToast(t.toast_create_board_failed);
      }
    } finally {
      setCreatingBoard(false);
    }
  };

  // Handle drag end for library reordering
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = images.findIndex(img => img.id === active.id);
    const newIndex = images.findIndex(img => img.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Optimistically update UI
    const newImages = arrayMove(images, oldIndex, newIndex);
    setImages(newImages);
    
    // Persist to backend
    try {
      const imageIds = newImages.map(img => img.id);
      await libraryAPI.reorder(imageIds);
    } catch (err) {
      console.error('Error reordering library:', err);
      fetchImages();
      showToast(t.toast_reorder_failed);
    }
  }, [images, fetchImages, t]);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null); // { ids, deletable, skipped }
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const handleDelete = async (image) => {
    setDeleteConfirm(image);
  };
  
  const handleConfirmDelete = async () => {
    const image = deleteConfirm;
    if (!image) return;
    
    const deletedId = image.id;
    const isExternal = image.is_external;
    const usageCount = image.usage_count || 0;
    
    setDeleteConfirm(null);
    
    try {
      const { data } = await libraryAPI.delete(deletedId);
      
      // Remove from local state after confirmed server deletion
      setImages(prev => prev.filter(img => img.id !== deletedId));
      setSelectedImage(prev => (prev && prev.id === deletedId) ? null : prev);
      setPreviewImage(prev => (prev && prev.id === deletedId) ? null : prev);
      
      // Update counts locally
      setCounts(prev => {
        if (!prev) return prev;
        const updated = { ...prev };
        updated.total_all = Math.max(0, (updated.total_all || 0) - 1);
        if (isExternal) {
          updated.total_external = Math.max(0, (updated.total_external || 0) - 1);
        } else {
          updated.total_uploaded = Math.max(0, (updated.total_uploaded || 0) - 1);
        }
        if (usageCount > 0) {
          updated.total_used = Math.max(0, (updated.total_used || 0) - 1);
        } else {
          updated.total_unused = Math.max(0, (updated.total_unused || 0) - 1);
        }
        return updated;
      });
      
      if (data.removed_from_boards > 0) {
        showToast(fmt(t.toast_image_deleted_with_boards, { count: data.removed_from_boards }));
      } else {
        showToast(t.toast_image_deleted);
      }
    } catch (err) {
      showToast(t.toast_delete_failed);
    }
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  // Open confirmation for bulk delete — classifies selection into deletable (unused) vs skipped (in boards)
  const handleBulkDeleteRequest = () => {
    if (selectedIds.size === 0) return;
    const selectedArr = [...selectedIds];
    const selectedImages = images.filter(img => selectedIds.has(img.id));
    const deletable = selectedImages.filter(img => (img.usage_count || 0) === 0).map(img => img.id);
    const skipped = selectedImages.filter(img => (img.usage_count || 0) > 0).map(img => img.id);
    setBulkDeleteConfirm({
      total: selectedArr.length,
      deletable,
      skipped,
    });
  };

  const handleConfirmBulkDelete = async () => {
    if (!bulkDeleteConfirm) return;
    const { deletable, skipped } = bulkDeleteConfirm;

    setBulkDeleting(true);
    let deletedCount = 0;
    const deletedIds = new Set();
    try {
      // Delete in parallel for speed; collect which succeeded
      const results = await Promise.allSettled(
        deletable.map(id => libraryAPI.delete(id).then(() => id))
      );
      results.forEach(r => {
        if (r.status === 'fulfilled') {
          deletedIds.add(r.value);
          deletedCount++;
        }
      });

      // Remove deleted images from local state
      if (deletedCount > 0) {
        setImages(prev => prev.filter(img => !deletedIds.has(img.id)));
        setCounts(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated.total_all = Math.max(0, (updated.total_all || 0) - deletedCount);
          // Recompute by type from deleted images
          const deletedImgs = images.filter(img => deletedIds.has(img.id));
          const extCount = deletedImgs.filter(img => img.is_external).length;
          const upCount = deletedCount - extCount;
          updated.total_external = Math.max(0, (updated.total_external || 0) - extCount);
          updated.total_uploaded = Math.max(0, (updated.total_uploaded || 0) - upCount);
          updated.total_unused = Math.max(0, (updated.total_unused || 0) - deletedCount);
          return updated;
        });
        if (selectedImage && deletedIds.has(selectedImage.id)) setSelectedImage(null);
        if (previewImage && deletedIds.has(previewImage.id)) setPreviewImage(null);
      }

      // Update selection — keep skipped ones selected so user can see what wasn't deleted
      setSelectedIds(new Set(skipped));

      // Toast messaging
      if (skipped.length > 0 && deletedCount > 0) {
        showToast(fmt(t.toast_bulk_deleted_with_skipped, { count: deletedCount, skipped: skipped.length }));
      } else if (skipped.length > 0 && deletedCount === 0) {
        showToast(t.toast_bulk_all_used);
      } else if (deletedCount > 0) {
        showToast(fmt(t.toast_bulk_deleted, { count: deletedCount }));
        // Nothing left skipped — exit multi-select
        exitMultiSelect();
      } else {
        showToast(t.toast_bulk_no_deleted);
      }
    } catch (err) {
      showToast(t.toast_bulk_delete_failed);
    } finally {
      setBulkDeleting(false);
      setBulkDeleteConfirm(null);
    }
  };

  const handleSaveTags = async (imageId, newTags) => {
    try {
      const { data } = await tagsAPI.updateImageTags(imageId, newTags);
      setImages(prev => prev.map(img => img.id === imageId ? { ...img, tags: data.tags } : img));
      if (selectedImage?.id === imageId) {
        setSelectedImage(prev => ({ ...prev, tags: data.tags }));
      }
      if (previewImage?.id === imageId) {
        setPreviewImage(prev => ({ ...prev, tags: data.tags }));
      }
      fetchTags();
      setEditingTags(false);
      setEditTagInput('');
      showToast(t.toast_tags_updated);
    } catch (err) {
      showToast(t.toast_tags_update_failed);
    }
  };

  // Toggle a single collection membership from the preview modal.
  // Sends `add_collection_ids` or `remove_collection_ids` to the same
  // bulk endpoint that powers Phase 4, so both paths share one code
  // route (and we get validation for free).
  const handleToggleCollectionMembership = async (imageId, collectionId, add) => {
    const payload = { image_ids: [imageId] };
    if (add) payload.add_collection_ids = [collectionId];
    else payload.remove_collection_ids = [collectionId];
    try {
      await libraryAPI.bulkAssign(payload);
      // Optimistic — patch the two states that render collection chips.
      const patch = (img) => {
        if (!img || img.id !== imageId) return img;
        const set = new Set(img.collection_ids || []);
        if (add) set.add(collectionId); else set.delete(collectionId);
        return { ...img, collection_ids: Array.from(set) };
      };
      setImages((prev) => prev.map(patch));
      setPreviewImage((prev) => patch(prev));
      // Update sidebar counts (per-collection image_count) lazily.
      fetchCollections();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Failed to update collection');
    }
  };

  // External URL handlers
  const validateImageUrl = (url) => {
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    const urlLower = url.toLowerCase().split('?')[0];
    return validExtensions.some(ext => urlLower.endsWith(ext));
  };

  const handleStartEditUrl = (image) => {
    setSelectedImage(image);
    setEditingUrl(true);
    setEditUrlValue(image.external_url || '');
    setEditUrlPreview(image.external_url || null);
    setEditUrlError(null);
  };

  const handleSaveEditUrl = async () => {
    if (!editUrlValue.trim() || !selectedImage) return;
    if (!validateImageUrl(editUrlValue)) {
      setEditUrlError(t.lib_url_ext_error);
      return;
    }
    setSavingUrl(true);
    try {
      const { data } = await libraryAPI.update(selectedImage.id, { external_url: editUrlValue.trim() });
      // Update state with the returned image
      setImages(prev => prev.map(img => img.id === selectedImage.id ? { ...data, usage_count: img.usage_count, boards_using: img.boards_using } : img));
      setSelectedImage(prev => ({ ...prev, ...data }));
      setEditingUrl(false);
      setEditUrlValue('');
      setEditUrlPreview(null);
      showToast(t.toast_url_updated);
    } catch (err) {
      setEditUrlError(err.response?.data?.detail || t.toast_url_update_failed);
    } finally {
      setSavingUrl(false);
    }
  };

  const handleExportTags = async () => {
    if (!activeTags.length) return;
    setExportingTags(true);
    try {
      const response = await libraryAPI.exportTags(activeTags);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeTags = activeTags.slice(0, 3).join('_').replace(/[^\w_-]/g, '');
      const brandPrefix = (branding?.workspace_name || 'Maestra').replace(/\s+/g, '_').replace(/[^\w_-]/g, '') || 'Maestra';
      link.setAttribute('download', `${brandPrefix}_Library_${safeTags}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast(t.toast_library_export);
    } catch (err) {
      showToast(err.response?.data?.detail || t.toast_export_failed);
    } finally {
      setExportingTags(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setExternalUrl(url);
    setUrlError(null);
    setUrlPreview(null);
    
    if (url.trim() && validateImageUrl(url)) {
      // Show preview
      setUrlPreview(url);
    }
  };

  const handleAddExternalUrl = async () => {
    if (!externalUrl.trim()) return;
    
    if (!validateImageUrl(externalUrl)) {
      setUrlError(t.lib_url_ext_error);
      return;
    }
    
    setAddingExternal(true);
    try {
      await libraryAPI.addExternal(externalUrl.trim());
      setExternalUrl('');
      setUrlPreview(null);
      setShowUrlInput(false);
      fetchImages();
      showToast(t.toast_external_added);
    } catch (err) {
      console.error('Error adding external:', err);
      setUrlError(err.response?.data?.detail || t.toast_external_add_failed);
    } finally {
      setAddingExternal(false);
    }
  };

  // Count by type
  const uploadedCount = counts.total_uploaded || 0;
  const externalCount = counts.total_external || 0;
  const allCount = counts.total_all || 0;

  return (
    <div className="dashboard fade-in">
      <header className="dashboard-header">
        <div>
          <p style={{ 
            fontFamily: 'Playfair Display, serif',
            fontSize: '12px',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '8px'
          }}>{ branding?.workspace_name || 'Maestra' }</p>
          <h1 className="dashboard-title">{t.lib_title}</h1>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" data-testid="library-back-workspace" onClick={() => navigate('/dashboard')}>
            {t.lib_back_to_moodboards}
          </button>
        </div>
      </header>

      {/* Archive Mode banner — calm read-only notice when subscription expired */}
      <ArchiveModeBanner archive={archiveMode} />

      {/* Storage warning banner — only when ≥80% used */}
      <StorageWarningBanner />

      {/* Batch upload progress / summary card. Renders nothing when idle. */}
      <BatchUploadStatus
        state={libraryBatch.state}
        label="image"
        t={t}
        onRetryFailed={libraryBatch.retryFailed}
        onDismiss={libraryBatch.dismiss}
      />

      {/* ────────────────────────────────────────────────────────────
          Refined two-step upload flow.

          Calm editorial spacing replaces the previous stacked
          "dropzone → tags → external link" surface. The studio now:
            1. Adds files (drop / choose / link) into a staging area
            2. Assigns tags that apply uniformly to every staged item
            3. Saves the whole batch with a clear count label
          ──────────────────────────────────────────────────────── */}
      {(() => {
        const pendingFileCount = pendingFiles.length;
        const hasLinkReady = !!urlPreview && !urlError && externalUrl.trim();
        const totalPending = pendingFileCount + (hasLinkReady ? 1 : 0);
        const hasAnyPending = totalPending > 0;

        // Section label used at the top of each editorial block.
        const StepLabel = ({ children, num }) => (
          <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{
              fontFamily: 'Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: '13px',
              color: 'var(--kahla-text-light)',
              letterSpacing: '0.02em',
            }}>{num}</span>
            <span style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '15px',
              color: 'var(--kahla-charcoal, var(--kahla-text))',
              letterSpacing: '-0.005em',
            }}>{children}</span>
          </div>
        );

        return (
          <div data-testid="library-upload-flow" style={{ marginBottom: '32px' }}>
            {/* ── STEP 1 — Add images ─────────────────────────── */}
            <section style={{ marginBottom: hasAnyPending ? '28px' : '18px' }}>
              <StepLabel num="1.">{t.lib_step_add_images || 'Add images'}</StepLabel>

              {/* Dropzone — calmer styling: thinner border, no loud
                  background. Drag-active state still tints subtly. */}
              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'active' : ''}`}
                data-testid="library-upload-zone"
                style={{
                  marginBottom: 0,
                  padding: '36px 24px',
                  borderStyle: 'dashed',
                  borderWidth: '1px',
                  borderColor: 'var(--kahla-border)',
                  background: isDragActive ? '#faf6ec' : 'transparent',
                  transition: 'background 0.2s ease, border-color 0.2s ease',
                  cursor: 'pointer',
                }}
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                    <span style={{ fontSize: '13px', color: 'var(--kahla-text-light)' }}>{t.lib_uploading}</span>
                  </div>
                ) : isDragActive ? (
                  <p style={{ fontSize: '15px', color: 'var(--kahla-taupe)', textAlign: 'center', margin: 0 }}>{t.lib_drop_active}</p>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{
                      fontFamily: 'Playfair Display, serif',
                      fontSize: '17px',
                      color: 'var(--kahla-text)',
                      margin: '0 0 6px',
                      letterSpacing: '-0.005em',
                    }}>{t.lib_drop_title}</p>
                    <p style={{
                      fontSize: '12px',
                      color: 'var(--kahla-text-light)',
                      margin: '0 0 18px',
                      fontStyle: 'italic',
                    }}>{t.lib_drop_subtitle}</p>

                    {/* Three calm CTAs, equal weight. The wrapper
                        dropzone already opens the file picker on click,
                        so the "Choose files" cue simply visualises that
                        affordance. The link button stops propagation so
                        it never opens the picker. */}
                    <div
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', justifyContent: 'center' }}
                    >
                      <span
                        style={{
                          fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
                          color: 'var(--kahla-text-light)',
                          borderBottom: '1px solid var(--kahla-border)', paddingBottom: '3px',
                        }}
                        data-testid="lib-choose-files-cue"
                      >{t.lib_choose_files || 'Choose files'}</span>
                      <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
                        {t.lib_or_separator || 'or'}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setShowUrlInput(true); }}
                        data-testid="library-add-external-url-btn"
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
                          color: 'var(--kahla-text-light)',
                          borderBottom: '1px solid var(--kahla-border)', paddingBottom: '3px',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >{t.lib_add_from_link || 'Add from link'}</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Inline link composer — appears inside Step 1 so the
                  URL feels like just another way to add images. */}
              {showUrlInput && (
                <div
                  data-testid="library-external-url-section"
                  style={{
                    marginTop: '14px',
                    padding: '18px 20px',
                    background: '#faf8f5',
                    border: '1px solid var(--kahla-border)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                    <label style={{ fontFamily: 'Playfair Display, serif', fontSize: '14px', color: 'var(--kahla-text)', letterSpacing: '-0.005em' }}>
                      {t.lib_add_external_title}
                    </label>
                    <button
                      onClick={() => { setShowUrlInput(false); setExternalUrl(''); setUrlPreview(null); setUrlError(null); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'var(--kahla-text-light)', lineHeight: 1, padding: 4 }}
                      aria-label="Close link composer"
                    >
                      ✕
                    </button>
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '10px', fontStyle: 'italic', lineHeight: 1.5 }}>
                    {t.lib_external_helper}
                  </p>
                  <input
                    type="text"
                    value={externalUrl}
                    onChange={handleUrlChange}
                    placeholder={t.lib_link_input_placeholder || 'https://i.pinimg.com/.../image.jpg'}
                    data-testid="library-external-url-input"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '10px 12px',
                      border: urlError ? '1px solid #c75050' : '1px solid var(--kahla-border)',
                      backgroundColor: 'var(--kahla-white)',
                      fontSize: '13px',
                    }}
                  />
                  {urlError && (
                    <p style={{ color: '#c75050', fontSize: '11px', marginTop: '8px' }}>{urlError}</p>
                  )}
                  {urlPreview && (
                    <div style={{ marginTop: '12px' }}>
                      <p style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginBottom: '6px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t.lib_preview_label}</p>
                      <div style={{
                        width: '120px', height: '120px',
                        border: '1px solid var(--kahla-border)',
                        overflow: 'hidden',
                        backgroundColor: 'var(--kahla-white)',
                      }}>
                        <img
                          src={urlPreview}
                          alt="Preview"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={() => { setUrlPreview(null); setUrlError(t.lib_url_load_error); }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Staged files — calm chip row, no panel chrome. */}
              {(pendingFileCount > 0 || hasLinkReady) && (
                <div
                  data-testid="library-pending-batch"
                  style={{
                    marginTop: '14px',
                    display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
                  }}
                >
                  <span style={{
                    fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: 'var(--kahla-text-light)', marginRight: '4px',
                  }}>{t.lib_pending_files_label || 'Selected'}</span>
                  {pendingFiles.map((f, i) => (
                    <span
                      key={`${f.name}-${f.size}-${f.lastModified}-${i}`}
                      data-testid={`library-pending-file-${i}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '4px 10px',
                        background: 'var(--kahla-white)',
                        border: '1px solid var(--kahla-border)',
                        fontSize: '11px', color: 'var(--kahla-text-light)',
                        maxWidth: '220px',
                      }}
                    >
                      <span style={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '170px',
                      }}>{f.name}</span>
                      <button
                        onClick={() => removePendingFile(i)}
                        aria-label={t.lib_remove_pending || 'Remove'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--kahla-text-light)', padding: 0, fontSize: '12px', lineHeight: 1 }}
                      >×</button>
                    </span>
                  ))}
                  {hasLinkReady && (
                    <span
                      data-testid="library-pending-link"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '4px 10px',
                        background: 'var(--kahla-white)',
                        border: '1px solid var(--kahla-border)',
                        fontSize: '11px', color: 'var(--kahla-text-light)',
                        maxWidth: '260px',
                      }}
                    >
                      <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', fontSize: '11px' }}>
                        {t.lib_pending_link_label || 'Link'}
                      </span>
                      <span style={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px',
                      }}>{externalUrl}</span>
                      <button
                        onClick={() => { setExternalUrl(''); setUrlPreview(null); setUrlError(null); setShowUrlInput(false); }}
                        aria-label={t.lib_remove_pending || 'Remove'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--kahla-text-light)', padding: 0, fontSize: '12px', lineHeight: 1 }}
                      >×</button>
                    </span>
                  )}
                </div>
              )}
            </section>

            {/* ── STEP 2 — Add tags ───────────────────────────── */}
            {hasAnyPending && (
              <section style={{ marginBottom: '28px' }} data-testid="library-step-add-tags">
                <StepLabel num="2.">{t.lib_step_add_tags || 'Add tags for this upload'}</StepLabel>
                <input
                  value={uploadTags}
                  onChange={(e) => setUploadTags(e.target.value)}
                  placeholder={t.lib_tags_for_batch_placeholder || t.lib_upload_tags_placeholder}
                  data-testid="upload-tags-input"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 14px', fontSize: '13px',
                    border: '1px solid var(--kahla-border)', outline: 'none',
                    backgroundColor: 'var(--kahla-white)',
                  }}
                />
                <p style={{
                  margin: '8px 0 0',
                  fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic', lineHeight: 1.5,
                }}>{t.lib_tags_for_batch_help || 'These tags will apply to every image in this batch — files and link.'}</p>

                {allTags.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <p style={{
                      fontSize: '9px', letterSpacing: '0.18em', textTransform: 'uppercase',
                      color: 'var(--kahla-text-light)', marginBottom: '8px',
                    }}>{t.lib_recent_tags || 'Recent tags'}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {allTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => setUploadTags(prev => {
                            const existing = prev.split(',').map(s => s.trim()).filter(Boolean);
                            if (existing.includes(tag)) return prev;
                            return existing.length ? `${prev}, ${tag}` : tag;
                          })}
                          style={{
                            padding: '3px 10px', fontSize: '11px', cursor: 'pointer',
                            border: '1px solid var(--kahla-border)', background: 'transparent',
                            color: 'var(--kahla-text-light)', letterSpacing: '0.02em',
                            fontFamily: 'inherit',
                          }}
                          data-testid={`upload-tag-suggest-${tag}`}
                        >+ {tag}</button>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* ── STEP 3 — Save to library ────────────────────── */}
            {hasAnyPending && (
              <section data-testid="library-step-save">
                <StepLabel num="3.">{t.lib_step_save || 'Save to library'}</StepLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  <button
                    className="btn-primary"
                    onClick={commitBatch}
                    disabled={uploading || addingExternal}
                    data-testid="library-save-batch-btn"
                    style={{ fontSize: '12px', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '12px 24px' }}
                  >
                    {(uploading || addingExternal)
                      ? (t.lib_adding || 'Adding...')
                      : (totalPending === 1
                          ? (t.lib_save_button_single || 'Add 1 image to Library')
                          : (t.lib_save_button_many || 'Add {count} images to Library').replace('{count}', String(totalPending))
                        )}
                  </button>
                  <button
                    onClick={clearBatch}
                    data-testid="library-clear-batch-btn"
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase',
                      color: 'var(--kahla-text-light)', cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >{t.lib_clear_batch || 'Clear'}</button>
                </div>
              </section>
            )}
          </div>
        );
      })()}

      {/* ── Library Collections rail ─────────────────────────────
          Top-level organisation layer above tags. Clicking a chip
          scopes every request to that collection; "All" clears the
          scope, "Uncategorized" surfaces items outside any collection. */}
      <CollectionRail
        collections={collections}
        activeCollection={activeCollection}
        onSelect={setActiveCollection}
        totalAll={totalAllCount}
        uncategorizedCount={uncategorizedCount}
        onCreate={async (name) => {
          const raw = (name || '').trim();
          if (!raw) return;
          try {
            const { data } = await libraryAPI.collections.create(raw);
            setCollections((prev) => [...prev, data].sort(
              (a, b) => (a.order ?? 0) - (b.order ?? 0),
            ));
          } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to create collection';
            setToast({ type: 'error', message: msg });
          }
        }}
        onRename={async (id, next) => {
          const raw = (next || '').trim();
          if (!raw) return;
          try {
            const { data } = await libraryAPI.collections.update(id, { name: raw });
            setCollections((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
          } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to rename collection';
            setToast({ type: 'error', message: msg });
          }
        }}
        onDelete={async (id) => {
          try {
            await libraryAPI.collections.remove(id);
            setCollections((prev) => prev.filter((c) => c.id !== id));
            if (activeCollection === id) setActiveCollection(null);
            fetchImages();
            fetchCollections();
          } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to delete collection';
            setToast({ type: 'error', message: msg });
          }
        }}
        t={t}
      />

      {/* Filters — technical/library status. Simple black pill controls,
          intentionally plainer than the cream Collection cards above. */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '24px',
        borderBottom: '1px solid var(--kahla-border)',
        paddingBottom: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <span style={{
          fontSize: 10,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
          marginRight: 2,
        }}>{t.lib_filters_label || 'Filters'}</span>
        <button 
          className={`btn-secondary ${filter === null ? 'active' : ''}`}
          style={{ 
            backgroundColor: filter === null ? 'var(--kahla-charcoal)' : 'transparent',
            color: filter === null ? 'var(--kahla-white)' : 'var(--kahla-charcoal)'
          }}
          onClick={() => setFilter(null)}
        >
          {t.lib_filter_all} ({allCount})
        </button>
        <button 
          className={`btn-secondary ${filter === 'uploaded' ? 'active' : ''}`}
          style={{ 
            backgroundColor: filter === 'uploaded' ? 'var(--kahla-charcoal)' : 'transparent',
            color: filter === 'uploaded' ? 'var(--kahla-white)' : 'var(--kahla-charcoal)'
          }}
          onClick={() => setFilter('uploaded')}
        >
          {t.lib_filter_uploaded} ({uploadedCount})
        </button>
        <button 
          className={`btn-secondary ${filter === 'external' ? 'active' : ''}`}
          style={{ 
            backgroundColor: filter === 'external' ? 'var(--kahla-charcoal)' : 'transparent',
            color: filter === 'external' ? 'var(--kahla-white)' : 'var(--kahla-charcoal)'
          }}
          onClick={() => setFilter('external')}
        >
          {t.lib_filter_external} ({externalCount})
        </button>
        <button 
          className={`btn-secondary ${filter === 'used' ? 'active' : ''}`}
          style={{ 
            backgroundColor: filter === 'used' ? 'var(--kahla-charcoal)' : 'transparent',
            color: filter === 'used' ? 'var(--kahla-white)' : 'var(--kahla-charcoal)'
          }}
          onClick={() => setFilter('used')}
        >
          {t.lib_filter_used}
        </button>
        <button 
          className={`btn-secondary ${filter === 'unused' ? 'active' : ''}`}
          style={{ 
            backgroundColor: filter === 'unused' ? 'var(--kahla-charcoal)' : 'transparent',
            color: filter === 'unused' ? 'var(--kahla-white)' : 'var(--kahla-charcoal)'
          }}
          onClick={() => setFilter('unused')}
        >
          {t.lib_filter_unused}
        </button>
      </div>

      {/* Tags — the core way of finding images: what the image contains.
          Prominent heading + optional search, chips stay lightweight so
          many tags can be combined at once. */}
      {allTags.length > 0 && (
        <div style={{ marginBottom: '28px' }} data-testid="library-tag-filters">
          <div style={{
            display: 'flex', alignItems: 'center', gap: '14px',
            flexWrap: 'wrap', marginBottom: '10px',
          }}>
            <span style={{
              fontSize: 11, fontWeight: 500,
              color: 'var(--kahla-charcoal)',
              letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>
              {t.lib_tags_heading || 'Filter by tags'}
            </span>
            {allTags.length > 8 && (
              <input
                value={tagQuery}
                onChange={(e) => setTagQuery(e.target.value)}
                placeholder={t.lib_tags_search_placeholder || 'Search tags…'}
                data-testid="library-tag-search"
                style={{
                  border: 'none',
                  borderBottom: '1px solid var(--kahla-border)',
                  background: 'transparent',
                  fontSize: '12px',
                  color: 'var(--kahla-text)',
                  padding: '3px 2px',
                  width: 150,
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            )}
            {activeTags.length > 0 && (
              <button
                onClick={() => setActiveTags([])}
                style={{ 
                  padding: '3px 10px', fontSize: '11px', cursor: 'pointer',
                  border: '1px solid var(--kahla-border)', background: 'transparent',
                  color: 'var(--kahla-text-light)',
                }}
                data-testid="clear-tag-filter"
              >
                {t.lib_clear_all}
              </button>
            )}
            {activeTags.length > 0 && !(archiveMode && archiveMode.library_bulk_blocked) && (
              <button
                onClick={handleExportTags}
                disabled={exportingTags}
                data-testid="export-tags-btn"
                style={{
                  padding: '3px 12px',
                  fontSize: '11px',
                  cursor: exportingTags ? 'default' : 'pointer',
                  border: '1px solid var(--kahla-border)',
                  background: 'transparent',
                  color: 'var(--kahla-taupe)',
                  marginLeft: 'auto',
                  opacity: exportingTags ? 0.5 : 1,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => { if (!exportingTags) e.target.style.color = 'var(--kahla-charcoal)'; }}
                onMouseLeave={(e) => e.target.style.color = 'var(--kahla-taupe)'}
              >
                {exportingTags ? t.lib_exporting : (activeTags.length === 1 ? fmt(t.lib_download_one_tag, { tag: activeTags[0] }) : fmt(t.lib_download_n_tags, { count: activeTags.length }))}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {allTags
              .filter((tag) => activeTags.includes(tag) ||
                tag.toLowerCase().includes(tagQuery.trim().toLowerCase()))
              .map(tag => {
              const isActive = activeTags.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() => setActiveTags(prev => 
                    isActive ? prev.filter(t => t !== tag) : [...prev, tag]
                  )}
                  style={{ 
                    padding: '3px 12px', fontSize: '12px', cursor: 'pointer',
                    border: isActive ? '1px solid var(--kahla-charcoal)' : '1px solid var(--kahla-border)',
                    background: isActive ? 'var(--kahla-charcoal)' : 'transparent',
                    color: isActive ? 'var(--kahla-white)' : 'var(--kahla-text)',
                  }}
                  data-testid={`tag-filter-${tag}`}
                >
                  {tag}
                </button>
              );
            })}
            {activeTags.length > 1 && (
              <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
                {t.lib_tags_all_hint}
              </span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
          <div className="spinner"></div>
        </div>
      ) : images.length === 0 ? (
        <div
          className="empty-state"
          data-testid="library-empty-onboarding"
          style={{
            maxWidth: 760,
            margin: '40px auto',
            padding: '64px 56px',
            textAlign: 'left',
            background: '#ffffff',
            border: '1px solid var(--kahla-border)',
          }}
        >
          {filter ? (
            <>
              <h2 style={{
                fontFamily: 'Playfair Display, serif', fontSize: 28,
                fontWeight: 400, margin: '0 0 12px', color: 'var(--kahla-charcoal)',
              }}>{fmt(t.lib_no_filtered_title, { filter })}</h2>
              <p style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic', fontSize: 15, color: 'var(--kahla-text-light)',
                lineHeight: 1.7, margin: 0, maxWidth: 540,
              }}>
                {t.lib_no_filtered_hint}
              </p>
            </>
          ) : (
            <>
              <p style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
                color: 'var(--kahla-text-light)', margin: '0 0 14px', fontWeight: 300,
              }}>
                {t.lib_empty_eyebrow}
              </p>
              <h2 style={{
                fontFamily: 'Playfair Display, serif', fontSize: 'clamp(28px, 4vw, 38px)',
                fontWeight: 400, margin: '0 0 18px', color: 'var(--kahla-charcoal)',
                lineHeight: 1.18, letterSpacing: '-0.005em', maxWidth: 580,
              }}>
                {t.lib_empty_title}
              </h2>
              <p style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic', fontSize: 17, color: 'var(--kahla-text-light)',
                lineHeight: 1.7, margin: 0, maxWidth: 580,
              }}>
                {t.lib_empty_body}
              </p>

              <EditorialGuidance
                placement="library_intro"
                guidance={guidance}
                lang={workspaceLang}
                dismissed={dismissedGuidance}
                onDismissed={(p) => setDismissedGuidance((prev) => [...prev, p])}
                maxWidth={580}
              />
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '24px' }}>
          {/* Image Grid with Drag & Drop */}
          <div style={{ flex: 1 }}>
            {/* Reorder Mode Toggle */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              marginBottom: '12px',
              gap: '8px',
              alignItems: 'center'
            }}>
              {reorderMode && (
                <span style={{ fontSize: '12px', color: 'var(--kahla-taupe)', fontStyle: 'italic' }}>
                  {t.lib_drag_to_reorder}
                </span>
              )}
              {multiSelect && (
                <span style={{ fontSize: '12px', color: 'var(--kahla-taupe)' }}>
                  {fmt(t.lib_selected_count, { count: selectedIds.size })}
                </span>
              )}
              {!reorderMode && (
                <button
                  onClick={() => {
                    if (multiSelect) { exitMultiSelect(); }
                    else { setMultiSelect(true); setSelectedIds(new Set()); }
                  }}
                  className={multiSelect ? 'btn-primary' : 'btn-secondary'}
                  style={{ fontSize: '12px', padding: '6px 14px' }}
                  data-testid="library-multiselect-toggle"
                  data-onboarding="library_bulk_select"
                >
                  {multiSelect ? t.lib_exit_select : t.lib_select_btn}
                </button>
              )}
              {!multiSelect && (
                <button
                  onClick={() => setReorderMode(!reorderMode)}
                  className={reorderMode ? 'btn-primary' : 'btn-secondary'}
                  style={{ 
                    fontSize: '12px', 
                    padding: '6px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="library-reorder-toggle"
                >
                  {reorderMode ? t.lib_reorder_on : t.lib_reorder_btn}
                </button>
              )}
              {!multiSelect && !reorderMode && (
                <button
                  onClick={handleOpenDuplicatesFinder}
                  className="btn-secondary"
                  style={{
                    fontSize: '12px',
                    padding: '6px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  data-testid="library-find-duplicates-btn"
                  data-onboarding="library_find_duplicates"
                >
                  {t.lib_find_duplicates || 'Znajd\u017a duplikaty'}
                </button>
              )}
              {/* Re-open the dismissed details panel. Only surfaces on
                  desktop, where the panel lives in a sticky right column
                  and may have been closed deliberately. On narrow/mobile
                  viewports the bottom-sheet opens automatically every
                  time an image is tapped, so this button would be
                  redundant clutter at the top of the toolbar. */}
              {!isNarrow && selectedImage && !detailsOpen && (
                <button
                  type="button"
                  onClick={openDetails}
                  data-testid="library-show-details"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--kahla-border)',
                    color: 'var(--kahla-text)',
                    padding: '6px 12px',
                    fontSize: '11px',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'background-color 0.18s, border-color 0.18s',
                  }}
                  title={t.lib_show_details}
                >
                  {t.lib_show_details}
                </button>
              )}
            </div>
            
            {reorderMode ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={images.map(img => img.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="moodboard-grid library-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                    {images.map((image) => (
                    <SortableLibraryCard
                      key={image.id}
                      image={image}
                      isSelected={selectedImage?.id === image.id}
                      onClick={() => handleSelectImage(image)}
                      reorderEnabled={reorderMode}
                      allTags={allTags}
                      onSaveTags={handleSaveTags}
                      onDelete={handleDelete}
                      onEditUrl={handleStartEditUrl}
                      multiSelect={multiSelect}
                      isChecked={selectedIds.has(image.id)}
                      onToggleSelect={toggleSelectImage}
                      onAddToBoard={handleAddToBoard}
                      t={t}
                    />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div
                className="library-masonry"
                data-testid="library-masonry"
                style={{
                  // CSS-columns masonry — natural Pinterest flow. The
                  // column count adapts to viewport width via CSS below.
                  columnGap: 14,
                }}
              >
                {images.map((image) => (
                  <PinCard
                    key={image.id}
                    image={image}
                    isSelected={previewImage?.id === image.id}
                    onClick={() => setPreviewImage(image)}
                    onDelete={handleDelete}
                    onEditUrl={handleStartEditUrl}
                    onAddToBoard={handleAddToBoard}
                    multiSelect={multiSelect}
                    isChecked={selectedIds.has(image.id)}
                    onToggleSelect={toggleSelectImage}
                    onLongPress={(id) => {
                      // Enter selection mode with the long-pressed pin
                      // already checked — mirrors Photos/Pinterest UX.
                      setMultiSelect(true);
                      setSelectedIds(new Set([id]));
                      lastSelectedIdRef.current = id;
                    }}
                    t={t}
                  />
                ))}
              </div>
            )}
            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} style={{ height: '1px' }} />
            {loadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                <div className="spinner"></div>
              </div>
            )}
          </div>

          {/* Selected Image Details */}
          {selectedImage && detailsOpen && (
            <>
              {/* Mobile backdrop — taps anywhere outside the sheet to
                  close. Desktop layout (≥1100px) keeps the panel as a
                  sticky sidebar, no backdrop needed. */}
              {isNarrow && (
                <div
                  onClick={closeDetails}
                  data-testid="library-details-backdrop"
                  style={{
                    position: 'fixed', inset: 0, background: 'rgba(28, 22, 14, 0.32)',
                    zIndex: 40, transition: 'opacity 0.22s ease',
                  }}
                />
              )}
              <div
                data-testid="library-details-panel"
                style={isNarrow ? {
                  // Bottom sheet on small screens — slides up from the
                  // bottom, full width, rounded top edge. Quietly framed
                  // so the founder can tap an image deep in the grid and
                  // immediately get details without scrolling back up.
                  position: 'fixed', left: 0, right: 0, bottom: 0,
                  maxHeight: '85vh',
                  backgroundColor: 'var(--kahla-white)',
                  borderTop: '1px solid var(--kahla-border)',
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                  padding: '14px 20px 28px',
                  overflowY: 'auto',
                  zIndex: 41,
                  boxShadow: '0 -18px 48px rgba(28, 22, 14, 0.16)',
                  transition: 'transform 0.22s ease',
                } : {
                  // Desktop sticky sidebar — unchanged spacing/rhythm.
                  width: '300px',
                  backgroundColor: 'var(--kahla-white)',
                  border: '1px solid var(--kahla-border)',
                  padding: '20px',
                  height: 'fit-content',
                  position: 'sticky',
                  top: '20px',
                  transition: 'width 0.22s ease, opacity 0.22s ease',
                }}>
              {/* Drag handle affordance — purely visual cue that this is
                  a dismissible sheet on narrow viewports. */}
              {isNarrow && (
                <div
                  aria-hidden="true"
                  style={{
                    width: '40px', height: '4px', borderRadius: '2px',
                    background: 'var(--kahla-border)',
                    margin: '0 auto 14px',
                  }}
                />
              )}              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}>
                <h3 style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: '16px',
                  margin: 0,
                }}>
                  {t.lib_details_title}
                </h3>
                {/* Tiny editorial close affordance — matches the calm
                    aesthetic the rest of Maestra uses; not an enterprise
                    DAM inspector chrome. */}
                <button
                  type="button"
                  onClick={closeDetails}
                  data-testid="library-close-details"
                  aria-label={t.lib_close_details_aria}
                  title={t.lib_close_details}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--kahla-text-light)',
                    fontSize: '18px',
                    lineHeight: 1,
                    padding: '4px 6px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'color 0.18s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-text-light)'; }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ 
                width: '100%', 
                aspectRatio: '1',
                marginBottom: '16px',
                border: '1px solid var(--kahla-border)',
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#faf5f0',
              }}>
                <DetailImage 
                  src={selectedImage.image_data} 
                  alt={selectedImage.name}
                  isExternal={selectedImage.is_external}
                  onEditUrl={() => handleStartEditUrl(selectedImage)}
                  onDelete={() => handleDelete(selectedImage)}
                  t={t}
                />
              </div>
              
              {/* Type badge */}
              <div style={{
                display: 'inline-block',
                padding: '4px 10px',
                backgroundColor: selectedImage.is_external ? 'var(--kahla-taupe)' : 'var(--kahla-cream)',
                color: selectedImage.is_external ? 'white' : 'var(--kahla-charcoal)',
                fontSize: '10px',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: '16px'
              }}>
                {selectedImage.is_external ? t.lib_external_ref_badge : t.lib_uploaded_badge}
              </div>
              
              <div className="form-group">
                <label>{t.lib_name_label}</label>
                <p style={{ fontSize: '14px' }}>{selectedImage.name}</p>
              </div>
              
              {selectedImage.is_external && selectedImage.external_url && (
                <div className="form-group">
                  <label>{t.lib_source_url_label}</label>
                  {!editingUrl ? (
                    <div>
                      <p style={{ 
                        fontSize: '12px', 
                        wordBreak: 'break-all',
                        color: 'var(--kahla-text-light)',
                        marginBottom: '4px',
                      }}>
                        {selectedImage.external_url}
                      </p>
                      <button 
                        onClick={() => handleStartEditUrl(selectedImage)}
                        style={{ 
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '11px', color: 'var(--kahla-text-light)', padding: '4px 0',
                          textDecoration: 'underline',
                        }}
                        data-testid="edit-url-btn"
                      >
                        {t.lib_edit_url}
                      </button>
                    </div>
                  ) : (
                    <div style={{ marginTop: '6px' }}>
                      <input
                        value={editUrlValue}
                        onChange={(e) => {
                          setEditUrlValue(e.target.value);
                          setEditUrlError(null);
                          if (e.target.value.trim() && validateImageUrl(e.target.value)) {
                            setEditUrlPreview(e.target.value);
                          } else {
                            setEditUrlPreview(null);
                          }
                        }}
                        placeholder="https://..."
                        style={{ 
                          width: '100%', padding: '6px 10px', fontSize: '12px',
                          border: editUrlError ? '1px solid #c75050' : '1px solid var(--kahla-border)',
                          outline: 'none', marginBottom: '6px', boxSizing: 'border-box',
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditUrl(); if (e.key === 'Escape') setEditingUrl(false); }}
                        data-testid="edit-url-input"
                      />
                      {editUrlError && (
                        <p style={{ color: '#c75050', fontSize: '11px', marginBottom: '6px' }}>{editUrlError}</p>
                      )}
                      {editUrlPreview && (
                        <div style={{ marginBottom: '8px' }}>
                          <p style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginBottom: '4px' }}>{t.lib_preview_label}</p>
                          <div style={{ width: '100%', height: '100px', overflow: 'hidden', border: '1px solid var(--kahla-border)', backgroundColor: '#faf5f0' }}>
                            <img 
                              src={editUrlPreview} alt="Preview"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={() => { setEditUrlPreview(null); setEditUrlError(t.lib_url_load_error); }}
                            />
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button 
                          className="btn-primary" style={{ fontSize: '11px', padding: '4px 12px' }}
                          onClick={handleSaveEditUrl}
                          disabled={savingUrl || !editUrlPreview}
                          data-testid="save-url-btn"
                        >{savingUrl ? t.lib_saving_url : t.lib_save_url}</button>
                        <button 
                          className="btn-secondary" style={{ fontSize: '11px', padding: '4px 12px' }}
                          onClick={() => { setEditingUrl(false); setEditUrlValue(''); setEditUrlPreview(null); setEditUrlError(null); }}
                        >{t.cancel}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="form-group">
                <label>{t.lib_usage_label}</label>
                <p style={{ fontSize: '14px' }}>
                  {fmt(selectedImage.usage_count === 1 ? t.lib_used_in_n_moodboards : t.lib_used_in_n_moodboards_plural, { count: selectedImage.usage_count })}
                </p>
              </div>
              
              {selectedImage.boards_using?.length > 0 && (
                <div className="form-group">
                  <label>{t.lib_boards_using}</label>
                  <div style={{ marginTop: '8px' }}>
                    {selectedImage.boards_using.map((board) => (
                      <div 
                        key={board.id}
                        style={{ 
                          padding: '8px 12px',
                          backgroundColor: 'var(--kahla-cream-light)',
                          marginBottom: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <p style={{ fontWeight: '500' }}>{board.name}</p>
                        <p style={{ color: 'var(--kahla-text-light)', fontSize: '11px' }}>
                          {board.client_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tags */}
              <div className="form-group">
                <label>{t.lib_tags_section_label}</label>
                {!editingTags ? (
                  <div>
                    {selectedImage.tags?.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                        {selectedImage.tags.map(tag => (
                          <span key={tag} style={{
                            fontSize: '11px', padding: '2px 8px',
                            backgroundColor: 'var(--kahla-cream)', border: '1px solid var(--kahla-border)',
                            color: 'var(--kahla-text)',
                          }}>{tag}</span>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>{t.lib_no_tags}</p>
                    )}
                    <button 
                      onClick={() => { setEditingTags(true); setEditTagInput((selectedImage.tags || []).join(', ')); }}
                      style={{ 
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: '11px', color: 'var(--kahla-text-light)', padding: '4px 0',
                        marginTop: '4px', textDecoration: 'underline'
                      }}
                      data-testid="edit-tags-btn"
                    >
                      {t.lib_edit_tags}
                    </button>
                  </div>
                ) : (
                  <div style={{ marginTop: '6px' }}>
                    <input
                      value={editTagInput}
                      onChange={(e) => setEditTagInput(e.target.value)}
                      placeholder={t.lib_tags_comma_placeholder}
                      style={{ 
                        width: '100%', padding: '6px 10px', fontSize: '13px',
                        border: '1px solid var(--kahla-border)', outline: 'none',
                        marginBottom: '6px', boxSizing: 'border-box',
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') {
                        const tags = editTagInput.split(',').map(tt => tt.trim()).filter(Boolean);
                        handleSaveTags(selectedImage.id, tags);
                      }}}
                      data-testid="edit-tags-input"
                    />
                    {allTags.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                        {allTags.filter(tt => !editTagInput.split(',').map(s=>s.trim()).includes(tt)).slice(0, 8).map(tag => (
                          <button 
                            key={tag}
                            onClick={() => setEditTagInput(prev => {
                              const existing = prev.split(',').map(tt => tt.trim()).filter(Boolean);
                              if (existing.includes(tag)) return prev;
                              return existing.length ? `${prev}, ${tag}` : tag;
                            })}
                            style={{ 
                              padding: '1px 8px', fontSize: '10px', cursor: 'pointer',
                              border: '1px solid var(--kahla-border)', background: 'var(--kahla-cream)',
                              color: 'var(--kahla-text-light)',
                            }}
                          >+ {tag}</button>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button 
                        className="btn-primary" style={{ fontSize: '12px', padding: '4px 12px' }}
                        onClick={() => {
                          const tags = editTagInput.split(',').map(tt => tt.trim()).filter(Boolean);
                          handleSaveTags(selectedImage.id, tags);
                        }}
                        data-testid="save-tags-btn"
                      >{t.save}</button>
                      <button 
                        className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}
                        onClick={() => { setEditingTags(false); setEditTagInput(''); }}
                      >{t.cancel}</button>
                    </div>
                  </div>
                )}
              </div>

              <button 
                className="btn-secondary"
                style={{ 
                  width: '100%', 
                  marginTop: '16px',
                  color: '#C75050',
                  cursor: 'pointer'
                }}
                onClick={() => handleDelete(selectedImage)}
                data-testid="delete-library-image-btn"
              >
                {t.lib_delete_from_library}
              </button>
              
              {selectedImage.usage_count > 0 && (
                <p style={{ 
                  fontSize: '11px', 
                  color: '#C75050', 
                  marginTop: '8px',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  {fmt(selectedImage.usage_count === 1 ? t.lib_will_remove_from_n_boards : t.lib_will_remove_from_n_boards_plural, { count: selectedImage.usage_count })}
                </p>
              )}
            </div>
            </>
          )}
        </div>
      )}

      {/* Floating Action Bar (legacy) — replaced by the calmer
          `BulkActionBar` (Phase 4) which merges the old actions
          (Add to board, New board, Delete) with the new Collection
          / Tag bulk operations into a single toolbar. */}

      {toast && <div className="toast">{toast}</div>}
      
      {/* Board Selector Modal */}
      {boardSelectorModal && (
        <div
          data-testid="board-selector-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => { setBoardSelectorModal(null); setBoardSelectorImageId(null); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', padding: '32px', maxWidth: '480px', width: '90%',
              border: '1px solid var(--kahla-border)', maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <h3 style={{
              fontFamily: 'Playfair Display, serif', fontSize: '18px',
              marginBottom: '8px', color: 'var(--kahla-charcoal)'
            }}>
              {t.lib_board_selector_title}
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '20px' }}>
              {boardSelectorModal === 'single'
                ? t.lib_board_selector_single
                : fmt(selectedIds.size === 1 ? t.lib_board_selector_bulk : t.lib_board_selector_bulk_plural, { count: selectedIds.size })}
            </p>

            {boardsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                <div className="spinner" style={{ width: '24px', height: '24px' }}></div>
              </div>
            ) : boardsList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '16px' }}>
                  {t.lib_no_active_boards}
                </p>
                <button
                  className="btn-primary"
                  style={{ fontSize: '13px' }}
                  onClick={handleOpenCreateBoard}
                  data-testid="create-board-from-selector-btn"
                >
                  {t.lib_create_new_board}
                </button>
              </div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1, marginBottom: '16px' }}>
                {boardsList.map((board) => (
                  <button
                    key={board.id}
                    onClick={() => handleConfirmAddToBoard(board.id)}
                    disabled={addingToBoard}
                    data-testid={`board-option-${board.id}`}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '12px 16px', marginBottom: '4px',
                      border: '1px solid var(--kahla-border)', background: 'white',
                      cursor: addingToBoard ? 'wait' : 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!addingToBoard) e.currentTarget.style.backgroundColor = 'var(--kahla-cream-light)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--kahla-charcoal)' }}>
                      {board.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '2px' }}>
                      {board.client_name || t.lib_no_client_label} · {board.image_count || 0} {t.lib_images_label} · {board.current_status}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', borderTop: '1px solid var(--kahla-border)', paddingTop: '16px' }}>
              <button
                className="btn-secondary"
                style={{ fontSize: '12px', padding: '8px 16px' }}
                onClick={handleOpenCreateBoard}
                data-testid="open-create-board-btn"
              >
                {t.lib_new_board_btn}
              </button>
              <button
                className="btn-secondary"
                style={{ fontSize: '12px', padding: '8px 16px' }}
                onClick={() => { setBoardSelectorModal(null); setBoardSelectorImageId(null); }}
                data-testid="board-selector-cancel-btn"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Board Modal */}
      {createBoardModal && (
        <div
          data-testid="create-board-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => { setCreateBoardModal(false); setSuccessBoard(null); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', padding: '32px', maxWidth: '440px', width: '90%',
              border: '1px solid var(--kahla-border)',
            }}
          >
            {successBoard ? (
              <>
                <h3 style={{
                  fontFamily: 'Playfair Display, serif', fontSize: '18px',
                  marginBottom: '12px', color: 'var(--kahla-charcoal)'
                }}>
                  {t.lib_board_created_title}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--kahla-text)', lineHeight: '1.5', marginBottom: '8px' }}>
                  {fmt(successBoard.count === 1 ? t.lib_board_created_text : t.lib_board_created_text_plural, { name: successBoard.name, count: successBoard.count })}
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: '13px', padding: '8px 20px' }}
                    onClick={() => { setCreateBoardModal(false); setSuccessBoard(null); }}
                    data-testid="success-close-btn"
                  >
                    {t.lib_stay_in_library}
                  </button>
                  <button
                    className="btn-primary"
                    style={{ fontSize: '13px', padding: '8px 20px' }}
                    onClick={() => navigate(`/board/${successBoard.id}`)}
                    data-testid="success-go-to-board-btn"
                  >
                    {t.lib_open_board}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{
                  fontFamily: 'Playfair Display, serif', fontSize: '18px',
                  marginBottom: '16px', color: 'var(--kahla-charcoal)'
                }}>
                  {t.lib_create_board_title}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '20px' }}>
                  {multiSelect && selectedIds.size > 0
                    ? fmt(selectedIds.size === 1 ? t.lib_create_board_with_selection : t.lib_create_board_with_selection_plural, { count: selectedIds.size })
                    : boardSelectorImageId
                      ? t.lib_create_board_with_one
                      : t.lib_create_board_empty}
                </p>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '11px', color: 'var(--kahla-text-light)',
                    letterSpacing: '0.05em', textTransform: 'uppercase',
                    display: 'block', marginBottom: '6px'
                  }}>{t.lib_board_name_label}</label>
                  <input
                    value={newBoardName}
                    onChange={(e) => setNewBoardName(e.target.value)}
                    placeholder={t.lib_board_name_placeholder}
                    style={{
                      width: '100%', padding: '10px 12px', fontSize: '14px',
                      border: '1px solid var(--kahla-border)', outline: 'none',
                      boxSizing: 'border-box',
                    }}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter' && newBoardName.trim()) handleCreateBoardFromSelection(); }}
                    data-testid="new-board-name-input"
                  />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    fontSize: '11px', color: 'var(--kahla-text-light)',
                    letterSpacing: '0.05em', textTransform: 'uppercase',
                    display: 'block', marginBottom: '6px'
                  }}>{t.lib_client_name_label}</label>
                  <input
                    value={newBoardClient}
                    onChange={(e) => setNewBoardClient(e.target.value)}
                    placeholder={t.lib_client_name_placeholder}
                    style={{
                      width: '100%', padding: '10px 12px', fontSize: '14px',
                      border: '1px solid var(--kahla-border)', outline: 'none',
                      boxSizing: 'border-box',
                    }}
                    data-testid="new-board-client-input"
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: '13px', padding: '8px 20px' }}
                    onClick={() => { setCreateBoardModal(false); setSuccessBoard(null); }}
                    data-testid="create-board-cancel-btn"
                  >
                    {t.cancel}
                  </button>
                  <button
                    className="btn-primary"
                    style={{ fontSize: '13px', padding: '8px 20px' }}
                    onClick={handleCreateBoardFromSelection}
                    disabled={!newBoardName.trim() || creatingBoard}
                    data-testid="create-board-confirm-btn"
                  >
                    {creatingBoard ? t.lib_creating : t.lib_create_board}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div 
          data-testid="delete-confirm-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', padding: '32px', maxWidth: '400px', width: '90%',
              border: '1px solid var(--kahla-border)',
            }}
          >
            <h3 style={{ 
              fontFamily: 'Playfair Display, serif', fontSize: '18px', 
              marginBottom: '12px', color: 'var(--kahla-charcoal)' 
            }}>
              {t.lib_delete_title}
            </h3>
            {(deleteConfirm.usage_count || 0) > 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--kahla-text)', lineHeight: '1.5', marginBottom: '24px' }}>
                {fmt(deleteConfirm.usage_count === 1 ? t.lib_delete_used_warning : t.lib_delete_used_warning_plural, { count: deleteConfirm.usage_count })}
              </p>
            ) : (
              <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '24px' }}>
                {t.lib_delete_unused_text}
              </p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                className="btn-secondary" 
                style={{ fontSize: '13px', padding: '8px 20px' }}
                onClick={() => setDeleteConfirm(null)}
                data-testid="delete-cancel-btn"
              >
                {t.cancel}
              </button>
              <button 
                style={{ 
                  fontSize: '13px', padding: '8px 20px', cursor: 'pointer',
                  backgroundColor: '#C75050', color: 'white', border: 'none',
                }}
                onClick={handleConfirmDelete}
                data-testid="delete-confirm-btn"
              >
                {t.delete}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteConfirm && (
        <div
          data-testid="bulk-delete-confirm-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => !bulkDeleting && setBulkDeleteConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', padding: '32px', maxWidth: '440px', width: '90%',
              border: '1px solid var(--kahla-border)',
            }}
          >
            <h3 style={{
              fontFamily: 'Playfair Display, serif', fontSize: '18px',
              marginBottom: '12px', color: 'var(--kahla-charcoal)'
            }}>
              {fmt(bulkDeleteConfirm.deletable.length === 1 ? t.lib_bulk_delete_title : t.lib_bulk_delete_title_plural, { count: bulkDeleteConfirm.deletable.length })}
            </h3>

            {bulkDeleteConfirm.deletable.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--kahla-text)', lineHeight: '1.6', marginBottom: '24px' }}>
                {fmt(t.lib_bulk_delete_all_used, { count: bulkDeleteConfirm.total })}
              </p>
            ) : (
              <p style={{ fontSize: '13px', color: 'var(--kahla-text)', lineHeight: '1.6', marginBottom: '24px' }}>
                {fmt(bulkDeleteConfirm.deletable.length === 1 ? t.lib_bulk_delete_some : t.lib_bulk_delete_some_plural, { count: bulkDeleteConfirm.deletable.length })}
                {bulkDeleteConfirm.skipped.length > 0 && (
                  <>
                    <br /><br />
                    <span style={{ color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
                      {fmt(bulkDeleteConfirm.skipped.length === 1 ? t.lib_bulk_delete_skipped : t.lib_bulk_delete_skipped_plural, { count: bulkDeleteConfirm.skipped.length })}
                    </span>
                  </>
                )}
              </p>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                style={{ fontSize: '13px', padding: '8px 20px' }}
                onClick={() => setBulkDeleteConfirm(null)}
                disabled={bulkDeleting}
                data-testid="bulk-delete-cancel-btn"
              >
                {t.cancel}
              </button>
              {bulkDeleteConfirm.deletable.length > 0 && (
                <button
                  style={{
                    fontSize: '13px', padding: '8px 20px',
                    cursor: bulkDeleting ? 'wait' : 'pointer',
                    backgroundColor: '#C75050', color: 'white', border: 'none',
                    opacity: bulkDeleting ? 0.7 : 1,
                  }}
                  onClick={handleConfirmBulkDelete}
                  disabled={bulkDeleting}
                  data-testid="bulk-delete-confirm-btn"
                >
                  {bulkDeleting ? t.lib_deleting : fmt(t.lib_delete_n, { count: bulkDeleteConfirm.deletable.length })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Image Modal */}
      {dupModal && (
        <div 
          data-testid="duplicate-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => setDupModal(null)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', padding: '32px', maxWidth: '480px', width: '90%',
              border: '1px solid var(--kahla-border)',
            }}
          >
            <h3 style={{ 
              fontFamily: 'Playfair Display, serif', fontSize: '18px', 
              marginBottom: '16px', color: 'var(--kahla-charcoal)' 
            }}>
              {t.lib_dup_title}
            </h3>
            
            <p style={{ fontSize: '13px', color: 'var(--kahla-text)', lineHeight: '1.5', marginBottom: '20px' }}>
              {t.lib_dup_text}
            </p>
            
            {/* Show existing image preview */}
            <div style={{ 
              display: 'flex', gap: '16px', marginBottom: '20px',
              padding: '16px', backgroundColor: 'var(--kahla-cream-light)', 
              border: '1px solid var(--kahla-border)',
            }}>
              <div style={{ width: '80px', height: '80px', flexShrink: 0, overflow: 'hidden', border: '1px solid var(--kahla-border)' }}>
                <img 
                  src={dupModal.existing.image_data || dupModal.existing.image_thumb} 
                  alt="" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {dupModal.existing.name}
                </p>
                <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
                  {fmt((dupModal.existing.usage_count || 0) === 1 ? t.lib_dup_used_in_n : t.lib_dup_used_in_n_plural, { count: dupModal.existing.usage_count || 0 })}
                </p>
                {dupModal.existing.tags?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '6px' }}>
                    {dupModal.existing.tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: '9px', padding: '1px 6px',
                        backgroundColor: 'white', border: '1px solid var(--kahla-border)',
                        color: 'var(--kahla-text-light)',
                      }}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Optional tag input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '11px', color: 'var(--kahla-text-light)', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                {t.lib_dup_add_tags_label}
              </label>
              <input
                value={dupTagInput}
                onChange={(e) => setDupTagInput(e.target.value)}
                placeholder={t.lib_dup_tag_placeholder}
                style={{ 
                  width: '100%', padding: '8px 12px', fontSize: '13px',
                  border: '1px solid var(--kahla-border)', outline: 'none', boxSizing: 'border-box',
                }}
                data-testid="dup-tag-input"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                className="btn-secondary" 
                style={{ fontSize: '12px', padding: '8px 16px' }}
                onClick={handleDupUploadAnyway}
                data-testid="dup-upload-anyway-btn"
              >
                {t.lib_dup_upload_anyway}
              </button>
              <button 
                className="btn-primary"
                style={{ fontSize: '12px', padding: '8px 16px' }}
                onClick={handleDupUseExisting}
                data-testid="dup-use-existing-btn"
              >
                {t.lib_dup_use_existing}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library Spotlight — first-visit guided tour for the image library */}
      {(() => {
        const previewMode = typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('preview_onboarding') === '1';
        return (
          <DemoSpotlight
            active={librarySpotlightActive}
            context="library"
            placement="library_spotlight"
            forcePreview={previewMode}
            onClose={() => {
              setLibrarySpotlightActive(false);
              if (!previewMode) {
                setDismissedGuidance((prev) => Array.from(new Set([...prev, 'library_spotlight'])));
              }
            }}
          />
        );
      })()}

      {/* Library Preview Modal (Phase 3) — full-screen overlay above
          the gallery. The page underneath keeps its scroll & filter
          state so closing feels like a dismissal, not a navigation. */}
      {previewImage && (
        <LibraryPreviewModal
          image={
            // Always read the freshest tags/collections/usage from the
            // grid state so inline edits (tags, membership) appear
            // instantly in the modal.
            images.find((i) => i.id === previewImage.id) || previewImage
          }
          onClose={() => setPreviewImage(null)}
          onAddToBoard={handleAddToBoard}
          onDelete={handleDelete}
          onSaveTags={handleSaveTags}
          onEditUrl={handleStartEditUrl}
          onOpenBoard={(bid) => navigate(`/board/${bid}`)}
          onToggleCollection={handleToggleCollectionMembership}
          allTags={allTags}
          collections={collections}
          t={t}
        />
      )}

      {/* Duplicates Finder (Phase X) — two-step modal: scan → confirm.
          Sits above the gallery, keeps scroll position, quiet aesthetic. */}
      {duplicatesReport && (
        <div
          data-testid="library-duplicates-modal"
          onClick={() => !duplicatesRunning && setDuplicatesReport(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 250,
            background: 'rgba(20,18,15,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FBF8F2',
              maxWidth: 520, width: '100%',
              padding: '32px 36px',
              borderRadius: 4,
              boxShadow: '0 24px 48px rgba(20,18,15,0.22)',
            }}
          >
            <div style={{
              fontSize: 10, letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              marginBottom: 8,
            }}>
              {t.lib_duplicates_eyebrow || 'Library housekeeping'}
            </div>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 22, fontWeight: 400, lineHeight: 1.25,
              margin: '0 0 20px',
              color: 'var(--kahla-charcoal)',
            }}>
              {t.lib_duplicates_title || 'Znajd\u017a duplikaty'}
            </h2>

            {duplicatesLoading || !duplicatesReport.report ? (
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 15, color: 'var(--kahla-text-light)' }}>
                {t.lib_duplicates_scanning || 'Scanning your library...'}
              </div>
            ) : duplicatesReport.report.duplicate_count === 0 ? (
              <>
                <p style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic', fontSize: 16, lineHeight: 1.55,
                  color: 'var(--kahla-text)', margin: '0 0 24px',
                }}>
                  {t.lib_duplicates_none ||
                    'Twoja biblioteka jest czysta \u2014 nie znaleziono duplikat\u00f3w.'}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setDuplicatesReport(null)}
                    data-testid="library-duplicates-close"
                    style={{
                      padding: '9px 20px', fontSize: 11,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      background: 'var(--kahla-charcoal)', color: 'white',
                      border: 'none', cursor: 'pointer',
                    }}
                  >{t.lib_duplicates_ok || 'OK'}</button>
                </div>
              </>
            ) : (
              <>
                <p style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic', fontSize: 16, lineHeight: 1.55,
                  color: 'var(--kahla-text)', margin: '0 0 8px',
                }}>
                  {fmt(t.lib_duplicates_found ||
                    'Znaleziono {dup} duplikat\u00f3w w {groups} grupach.',
                    { dup: duplicatesReport.report.duplicate_count,
                      groups: duplicatesReport.report.group_count })}
                </p>
                <p style={{
                  fontSize: 12, color: 'var(--kahla-text-light)',
                  lineHeight: 1.6, margin: '0 0 24px',
                }}>
                  {t.lib_duplicates_description ||
                    'W ka\u017cdej grupie zachowamy najstarsze zdj\u0119cie. Tagi i kolekcje z pozosta\u0142ych kopii zostan\u0105 przeniesione na oryginale. Tablice klienta, kt\u00f3re u\u017cywa\u0142y duplikatu, zostan\u0105 przepi\u0119te na oryginale \u2014 bez utraty imagerii.'}
                </p>

                {/* Preview thumbnails from the first few groups */}
                <div style={{ marginBottom: 24 }}>
                  {duplicatesReport.report.groups.slice(0, 3).map((g, idx) => (
                    <div key={g.key} style={{
                      display: 'flex', gap: 6, alignItems: 'center',
                      marginBottom: idx === 2 ? 0 : 8,
                    }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {g.images.slice(0, 4).map((im, i) => (
                          <div key={im.id} style={{
                            width: 42, height: 42,
                            background: '#eee',
                            border: i === 0 ? '2px solid var(--kahla-charcoal)' : '1px solid var(--kahla-border)',
                            backgroundImage: im.image_thumb ? `url("${im.image_thumb}")` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }} title={i === 0 ? 'Survivor (kept)' : 'Duplicate (removed)'} />
                        ))}
                        {g.count > 4 && (
                          <div style={{
                            width: 42, height: 42,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, color: 'var(--kahla-text-light)',
                            border: '1px dashed var(--kahla-border)',
                          }}>+{g.count - 4}</div>
                        )}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--kahla-text-light)', marginLeft: 4 }}>
                        {g.kind === 'uploaded' ? (t.lib_duplicates_same_file || 'ten sam plik')
                          : g.kind === 'filename' ? (t.lib_duplicates_same_filename || 'ta sama nazwa pliku')
                          : (t.lib_duplicates_same_url || 'ten sam link')}
                      </span>
                    </div>
                  ))}
                  {duplicatesReport.report.group_count > 3 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--kahla-text-light)' }}>
                      {fmt(t.lib_duplicates_more_groups || '+ {n} kolejnych grup...',
                        { n: duplicatesReport.report.group_count - 3 })}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setDuplicatesReport(null)}
                    disabled={duplicatesRunning}
                    data-testid="library-duplicates-cancel"
                    style={{
                      padding: '9px 18px', fontSize: 11,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      background: 'transparent',
                      border: '1px solid var(--kahla-border)',
                      color: 'var(--kahla-text)', cursor: 'pointer',
                    }}
                  >{t.cancel || 'Anuluj'}</button>
                  <button
                    type="button"
                    onClick={handleConfirmDedupe}
                    disabled={duplicatesRunning}
                    data-testid="library-duplicates-confirm"
                    style={{
                      padding: '9px 20px', fontSize: 11,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      background: 'var(--kahla-charcoal)', color: 'white',
                      border: 'none',
                      cursor: duplicatesRunning ? 'wait' : 'pointer',
                      opacity: duplicatesRunning ? 0.7 : 1,
                    }}
                  >{duplicatesRunning
                    ? (t.lib_duplicates_removing || 'Usuwanie...')
                    : (t.lib_duplicates_confirm || 'Usu\u0144 duplikaty')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Action Bar (Phase 4) — bottom-anchored toolbar shown
          whenever the studio is in multi-select mode. Even at zero
          selection the bar is present as a state hint that Select is
          active; actions disable themselves gracefully. */}
      {multiSelect && (
        <BulkActionBar
          count={selectedIds.size}
          totalImages={images.length}
          collections={collections}
          activeSelectionTags={(() => {
            // Union of tags across the currently-selected images so
            // the "Remove tags" popover offers only relevant options.
            const set = new Set();
            images.forEach((img) => {
              if (selectedIds.has(img.id)) {
                (img.tags || []).forEach((tg) => set.add(tg));
              }
            });
            return Array.from(set).sort();
          })()}
          onAddToBoard={handleAddSelectedToBoard}
          onCreateBoard={() => { setCreateBoardModal(true); setNewBoardName(''); setNewBoardClient(''); setSuccessBoard(null); }}
          collectionStates={(() => {
            const sel = images.filter((img) => selectedIds.has(img.id));
            const states = {};
            collections.forEach((c) => {
              const n = sel.reduce((acc, img) => acc + ((img.collection_ids || []).includes(c.id) ? 1 : 0), 0);
              states[c.id] = n === 0 ? 'none' : n === sel.length ? 'all' : 'some';
            });
            return states;
          })()}
          onToggleCollectionBulk={handleBulkToggleCollection}
          onMoveToCollection={handleBulkMoveToCollection}
          onAddTags={handleBulkAddTags}
          onRemoveTags={handleBulkRemoveTags}
          onDelete={handleBulkDeleteRequest}
          onSelectAll={handleSelectAll}
          onCancel={exitMultiSelect}
          t={t}
        />
      )}
    </div>
  );
};

export default ImageLibrary;
